import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArchiveOutlined as ArchiveOutlinedIcon, AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon, AutorenewOutlined as AutorenewOutlinedIcon, CalendarMonthOutlined as CalendarMonthOutlinedIcon, CallOutlined as CallOutlinedIcon, ChecklistOutlined as ChecklistOutlinedIcon, DescriptionOutlined as DescriptionOutlinedIcon, DirectionsCarOutlined as DirectionsCarOutlinedIcon, EventAvailableOutlined as EventAvailableOutlinedIcon, FactCheckOutlined as FactCheckOutlinedIcon, FeedbackOutlined as FeedbackOutlinedIcon, HomeOutlined as HomeOutlinedIcon, LocalShippingOutlined as LocalShippingOutlinedIcon, PaidOutlined as PaidOutlinedIcon, PendingActionsOutlined as PendingActionsOutlinedIcon, PersonOutlineOutlined as PersonOutlineOutlinedIcon, PlaceOutlined as PlaceOutlinedIcon, PlayCircleOutlineOutlined as PlayCircleOutlineOutlinedIcon, RateReviewOutlined as RateReviewOutlinedIcon, RestaurantMenuOutlined as RestaurantMenuOutlinedIcon, TaskAltOutlined as TaskAltOutlinedIcon, VerifiedOutlined as VerifiedOutlinedIcon, ViewInArOutlined as ViewInArOutlinedIcon, WarehouseOutlined as WarehouseOutlinedIcon } from "../../../components/icons";
import { Inventory2Outlined as Inventory2OutlinedIcon } from "../../../components/icons";
import dayjs from "dayjs";
import {
  completeFuneralPlanning,
  completeFuneralPlanningTask,
  followUpFuneralPlanningTask,
  confirmFuneralPlanning,
  getFuneralPlanningByCase,
  getFuneralPlans,
  markFuneralPlanningReady,
  requestFuneralPurchaseOrder,
  requestFuneralPlanningTask,
  saveFuneralHandoverChecklist,
  saveFuneralPlanning,
  searchClaim,
  triggerFuneralPlanningFeedback,
} from "../../../services/dashboard";
import { useAuthStore } from "../../../store/useAuthStore";
import { useSessionStore } from "../../../store/useSessionStore";
import { useUserStore } from "../../../store/useUserStore";
import { useToast } from "../../../hooks/useToast";
import { StandardMetricCard, StatusDotChip, StandardPager, standardPrimaryBtnSx, type Tone } from "../ifas/components/StandardUI";
import { OperationsSectionHeader } from "../ifas/components/OperationsUI";

const statusTone: Record<string, Tone> = {
  PLANNING_REQUIRED: "amber",
  IN_PROGRESS: "orange",
  AWAITING_DOCUMENTS: "amber",
  AWAITING_PAYMENT: "amber",
  READY_FOR_FUNERAL: "green",
  CONFIRMED: "green",
  IN_EXECUTION: "blue",
  COMPLETED: "green",
  FEEDBACK_PENDING: "amber",
  CLAIM_DECLINED: "red",
};

const orange = "#F05A00";
const orangeSoft = "#FFF3E8";
const green = "#15803D";
const amber = "#B45309";
const blue = "#0369A1";
const ink = "#111827";
const muted = "#64748B";
const border = "#E5E7EB";
const surface = "#FFFFFF";
const pageBg = "#F3F6F8";
const softShadow = "0 10px 30px rgba(15, 23, 42, 0.07)";
const subtleShadow = "0 4px 14px rgba(15, 23, 42, 0.05)";

const orangeButtonSx = {
  "&&": {
    bgcolor: `${orange} !important`,
    color: "#fff !important",
    borderColor: `${orange} !important`,
  },
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 900,
  "&:hover, &:focus, &:active": {
    bgcolor: "#D94F00 !important",
    color: "#fff !important",
    borderColor: "#D94F00 !important",
  },
  "&.Mui-disabled": {
    bgcolor: "#E2E8F0 !important",
    color: "#64748B !important",
    borderColor: "#E2E8F0 !important",
  },
};

const neutralButtonSx = {
  "&&": {
    bgcolor: "#fff !important",
    color: `${ink} !important`,
    borderColor: "#CBD5E1 !important",
  },
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 900,
  "&:hover, &:focus, &:active": {
    bgcolor: "#F8FAFC !important",
    borderColor: "#94A3B8 !important",
    color: `${ink} !important`,
  },
  "&.Mui-disabled": {
    bgcolor: "#EEF2F7 !important",
    borderColor: "#E2E8F0 !important",
    color: "#64748B !important",
  },
};

const statuses = ["PLANNING_REQUIRED", "IN_PROGRESS", "AWAITING_DOCUMENTS", "AWAITING_PAYMENT", "READY_FOR_FUNERAL", "CONFIRMED", "IN_EXECUTION", "COMPLETED", "FEEDBACK_PENDING", "CLAIM_DECLINED", "CLOSED"];
const categories = ["SCHEME", "SCHEME_TOP_UP", "CASH", "MASS", "STATE", "SUBCONTRACTED"];
const documentStatuses = ["NOT_STARTED", "IN_PROGRESS", "RECEIVED", "COMPLETED"];
const repatriationTypes = ["NONE", "DOMESTIC", "INTERNATIONAL"];
const approvalStatuses = ["NOT_REQUIRED", "PENDING", "APPROVED", "REJECTED"];

const taskTeamMap: Record<string, string> = {
  BODY_REMOVAL: "REMOVAL_CALL_CENTRE_AGENT",
  WAREHOUSE_PREPARATION: "WAREHOUSE_SUPERVISOR",
  WAREHOUSE_TRANSPORT: "FLEET_CENTRE_COORDINATOR",
  FLEET: "FLEET_CENTRE_COORDINATOR",
  CATERING: "CATERING",
  TOMBSTONE: "TOMBSTONE",
  BODY_DISPATCH: "MORTUARY_SUPERVISOR",
  POST_FUNERAL_FEEDBACK: "FUNERAL_PLANNER",
};

const systemManagedTaskTypes = new Set([
  "BODY_REMOVAL",
  "WAREHOUSE_PREPARATION",
  "WAREHOUSE_TRANSPORT",
  "FLEET",
  "BODY_DISPATCH",
  "POST_FUNERAL_FEEDBACK",
]);

const defaults: any = {
  plannerName: "",
  status: "PLANNING_REQUIRED",
  funeralCategory: "",
  internalClientName: "",
  externalClientName: "",
  familyRepresentativeName: "",
  familyRepresentativeContact: "",
  representativeRelationship: "",
  clientRequirements: "",
  bodyLocationType: "",
  bodyLocationName: "",
  bodyLocationAddress: "",
  icebolethuMortuaryName: "",
  bodyRemovalRequired: false,
  bodyRemovalAuthorized: false,
  bodyRemovalAuthorizationNotes: "",
  bi1663Status: "NOT_STARTED",
  deathCertificateStatus: "NOT_STARTED",
  doctorSignedBi1663: false,
  bi1663AllPagesCompleted: false,
  deceasedIdReceived: false,
  informantIdReceived: false,
  repatriationType: "NONE",
  repatriationMode: "",
  deathRegistered: false,
  caseNumberOpened: false,
  postMortemConducted: false,
  inspectorReportSigned: false,
  embalmed: false,
  embalmingCertificateReceived: false,
  nonInfectiousLetterReceived: false,
  exportPermitReceived: false,
  clearanceCertificateReceived: false,
  statutoryDocumentsReady: false,
  policyNumber: "",
  packageDetails: "",
  additionalItems: "",
  cateringNeeded: false,
  tombstoneNeeded: false,
  decorColours: "",
  vehicleColourPreference: "",
  familyCarRequired: false,
  hearseVehicleCount: 1,
  familyVehicleCount: 0,
  additionalVehicleCount: 0,
  subcontractorName: "",
  subcontractorContact: "",
  subcontractorQuotationReference: "",
  subcontractingRequired: false,
  subcontractingReason: "",
  subcontractorReputationConfirmed: false,
  principalPackageUnderstood: false,
  gcooApprovalStatus: "NOT_REQUIRED",
  gcooApprovedBy: "",
  gcooApprovalNotes: "",
  familyRepresentativeAcceptedSubcontractor: false,
  massFuneralVenue: "",
  massFuneralFamilyCount: "",
  massFuneralPackageConsistency: "",
  massFuneralAdditionalItems: "",
  stateGovernmentSphere: "",
  stateSafetyRequirements: "",
  stateExpectedAttendees: "",
  stateSteeringCommittee: "",
  stateDryRunDate: "",
  stateProtocolOwner: "",
  stateVenueType: "",
  stateParkingOwner: "",
  quotationNumber: "",
  quotationGenerated: false,
  quotationAccepted: false,
  quotationAmount: "",
  invoiceNumber: "",
  invoiceGenerated: false,
  invoiceAmount: "",
  paymentConfirmed: false,
  proofOfPaymentReference: "",
  purchaseOrderNumber: "",
  purchaseOrderRequired: false,
  purchaseOrderRequested: false,
  purchaseOrderRequestedBy: "",
  purchaseOrderStatus: "",
  funeralDate: "",
  funeralVenue: "",
  burialOrCemeteryDetails: "",
  undertakerName: "",
  handoverChecklistPrepared: false,
  materialChecklistPrepared: false,
  planningImplementationNotes: "",
  feedbackDueDate: "",
  feedbackNotes: "",
  tasks: [],
};

const taskIcons: Record<string, React.ReactNode> = {
  BODY_REMOVAL: <LocalShippingOutlinedIcon fontSize="small" />,
  WAREHOUSE_PREPARATION: <WarehouseOutlinedIcon fontSize="small" />,
  WAREHOUSE_TRANSPORT: <LocalShippingOutlinedIcon fontSize="small" />,
  FLEET: <DirectionsCarOutlinedIcon fontSize="small" />,
  CATERING: <RestaurantMenuOutlinedIcon fontSize="small" />,
  TOMBSTONE: <ViewInArOutlinedIcon fontSize="small" />,
  BODY_DISPATCH: <AssignmentTurnedInOutlinedIcon fontSize="small" />,
  POST_FUNERAL_FEEDBACK: <FeedbackOutlinedIcon fontSize="small" />,
};

const labelize = (value: string) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const statusColor = (value: string) => {
  const status = String(value || "").toUpperCase();
  if (status === "CLAIM_DECLINED") return "#B91C1C";
  if (["READY_FOR_FUNERAL", "CONFIRMED", "COMPLETED", "CLOSED"].includes(status)) return green;
  if (["AWAITING_DOCUMENTS", "AWAITING_PAYMENT", "FEEDBACK_PENDING"].includes(status)) return amber;
  if (status === "IN_EXECUTION") return blue;
  if (status === "IN_PROGRESS") return orange;
  return muted;
};

const claimStatusColor = (value: string) => {
  const status = String(value || "").toUpperCase();
  if (["APPROVED", "PAID", "CLOSED"].includes(status)) return "#15803D";
  if (status === "DECLINED") return "#B91C1C";
  if (["NEW", "REVIEWED", "VETTED"].includes(status)) return "#0369A1";
  return "#B45309";
};

const statusIcons: Record<string, React.ReactNode> = {
  PLANNING_REQUIRED: <PendingActionsOutlinedIcon sx={{ fontSize: 15 }} />,
  IN_PROGRESS: <AutorenewOutlinedIcon sx={{ fontSize: 15 }} />,
  AWAITING_DOCUMENTS: <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />,
  AWAITING_PAYMENT: <PaidOutlinedIcon sx={{ fontSize: 15 }} />,
  READY_FOR_FUNERAL: <TaskAltOutlinedIcon sx={{ fontSize: 15 }} />,
  CONFIRMED: <VerifiedOutlinedIcon sx={{ fontSize: 15 }} />,
  IN_EXECUTION: <PlayCircleOutlineOutlinedIcon sx={{ fontSize: 15 }} />,
  COMPLETED: <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 15 }} />,
  FEEDBACK_PENDING: <RateReviewOutlinedIcon sx={{ fontSize: 15 }} />,
  CLOSED: <ArchiveOutlinedIcon sx={{ fontSize: 15 }} />,
  CLAIM_DECLINED: <ArchiveOutlinedIcon sx={{ fontSize: 15 }} />,
};

const getInitials = (value: string) => {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
};

const hasText = (value: any) => String(value || "").trim().length > 0;
const isDoneDoc = (value: string) => ["RECEIVED", "COMPLETED"].includes(String(value || ""));
const isProgressDoc = (value: string) => ["IN_PROGRESS", "RECEIVED", "COMPLETED"].includes(String(value || ""));
const isStep10Like = (stepId: string) => stepId === "step10";

const Field = (props: any) => (
  <TextField
    fullWidth
    size="small"
    {...props}
    InputLabelProps={props.type === "date" ? { shrink: true } : props.InputLabelProps}
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        bgcolor: "#fff",
        fontSize: 14,
        minHeight: 42,
        "& fieldset": { borderColor: "#DDE3EA" },
        "&:hover fieldset": { borderColor: "#CBD5E1" },
        "&.Mui-focused fieldset": { borderColor: orange, borderWidth: "1px" },
      },
      "& .MuiInputLabel-root": { fontSize: 13, fontWeight: 800, color: "#475569" },
      "& .MuiFormHelperText-root": { ml: 0, fontSize: 11.5 },
      ...props.sx,
    }}
  />
);

const SelectField = ({ field, label, options, selectedPlan, updateField }: { field: string; label: string; options: string[]; selectedPlan: any; updateField: (field: string, value: any) => void }) => (
  <Field select label={label} value={selectedPlan?.[field] || ""} onChange={(event: any) => updateField(field, event.target.value)}>
    {options.map((option) => (
      <MenuItem key={option} value={option}>
        {labelize(option)}
      </MenuItem>
    ))}
  </Field>
);

const Toggle = ({ field, label, selectedPlan, updateField, disabled = false }: { field: string; label: string; selectedPlan: any; updateField: (field: string, value: any) => void; disabled?: boolean }) => (
  <FormControlLabel
    control={<Checkbox size="small" checked={Boolean(selectedPlan?.[field])} disabled={disabled} onChange={(event) => updateField(field, event.target.checked)} sx={{ color: orange, "&.Mui-checked": { color: orange } }} />}
    label={<Typography fontWeight={800} fontSize={13}>{label}</Typography>}
    sx={{ m: 0 }}
  />
);

const ServiceRequirementField = ({
  field,
  label,
  selectedPlan,
  updateField,
  sourceLinked = false,
}: {
  field: string;
  label: string;
  selectedPlan: any;
  updateField: (field: string, value: any) => void;
  sourceLinked?: boolean;
}) => (
  <Field
    select
    label={label}
    value={Boolean(selectedPlan?.[field]) ? "YES" : "NO"}
    disabled={sourceLinked}
    onChange={(event: any) => updateField(field, event.target.value === "YES")}
    helperText={sourceLinked ? "Linked from Case/Claim service selection." : "No linked service found; planner fallback capture is available."}
  >
    <MenuItem value="YES">Required</MenuItem>
    <MenuItem value="NO">Not required</MenuItem>
  </Field>
);

const Cell = ({ md = 12, children }: { md?: number; children: React.ReactNode }) => (
  <Box sx={{ gridColumn: { xs: "span 12", md: `span ${md}` } }}>{children}</Box>
);

const SectionCard = ({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) => (
  <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", p: 2, bgcolor: surface, boxShadow: subtleShadow }}>
    <Stack direction="row" alignItems="center" gap={1.25} mb={1.75}>
      <Box sx={{ width: 34, height: 34, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: orangeSoft, color: orange }}>{icon}</Box>
      <Typography fontWeight={950} fontSize={16}>{title}</Typography>
    </Stack>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 1.5 }}>{children}</Box>
  </Paper>
);

export default function FuneralPlanningPortal() {
  const [plans, setPlans] = useState<any[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [selectedWorkstream, setSelectedWorkstream] = useState("step1");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [claimPolicyNumber, setClaimPolicyNumber] = useState("");
  const [claimSearchResults, setClaimSearchResults] = useState<any[]>([]);
  const [claimSearchError, setClaimSearchError] = useState("");
  const [claimSearchLoading, setClaimSearchLoading] = useState(false);
  const initialLoadStartedRef = useRef(false);
  const { first_name, last_name, userId } = useAuthStore();
  const role = useUserStore((state) => state.role);
  const { userRole } = useSessionStore();
  const { showSuccessToast, showErrorToast } = useToast();
  const effectiveRole = userRole || role;

  const performedBy = useMemo(
    () => `${first_name || ""} ${last_name || ""}`.trim() || "Funeral Planner",
    [first_name, last_name],
  );
  const actor = { performedBy, roleAtTime: effectiveRole };

  const loadPlans = async (requestedPage = page) => {
    setLoading(true);
    const response: any = await getFuneralPlans({
      status: statusFilter || null,
      searchText: searchText || null,
      page: requestedPage,
      size: pageSize,
    });
    setLoading(false);
    if (response?.status === 200) {
      const rows = response.data?.plans || [];
      setPlans(rows);
      setCounts(response.data?.statusCounts || {});
      setTotalCount(response.data?.totalCount || 0);
    } else {
      showErrorToast(response?.data?.message || "Failed to load funeral plans.");
    }
  };

  useEffect(() => {
    if (initialLoadStartedRef.current) return;
    initialLoadStartedRef.current = true;
    void loadPlans(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchClaimStatus = async () => {
    const policyNumber = claimPolicyNumber.trim();
    if (!policyNumber) {
      setClaimSearchResults([]);
      setClaimSearchError("Enter a policy number to check the claim status.");
      return;
    }

    setClaimSearchLoading(true);
    setClaimSearchError("");
    setClaimSearchResults([]);
    const response: any = await searchClaim(policyNumber, "DEATH");
    setClaimSearchLoading(false);

    if (response?.status === 200) {
      const claims = Array.isArray(response.data?.claims) ? response.data.claims : [];
      if (claims.length === 0) {
        setClaimSearchError(`No death claim was found for policy ${policyNumber}.`);
        return;
      }
      setClaimSearchResults(claims);
      return;
    }

    if (response?.status === 404) {
      setClaimSearchError(`No death claim was found for policy ${policyNumber}.`);
      return;
    }
    setClaimSearchError(response?.data?.message || "Claim status is currently unavailable. Please try again.");
  };

  const openPlan = async (summary: any, resetStep = true) => {
    if (!summary?.caseId) return;
    setLoading(true);
    const response: any = await getFuneralPlanningByCase(summary.caseId);
    setLoading(false);
    if (response?.status === 200 && response.data) {
      setSelectedPlan({ ...defaults, ...response.data });
      if (resetStep) setSelectedWorkstream("step1");
    } else {
      showErrorToast(response?.data?.message || "Failed to load funeral plan.");
    }
  };

  useEffect(() => {
    const caseId = selectedPlan?.caseId;
    if (!caseId) return undefined;
    const timer = window.setInterval(async () => {
      const response: any = await getFuneralPlanningByCase(caseId);
      if (response?.status === 200 && response.data) {
        setSelectedPlan((current: any) => current?.caseId === caseId ? {
          ...current,
          status: response.data.status,
          tasks: response.data.tasks || [],
          cateringNeeded: response.data.cateringSyncedFromSource ? response.data.cateringNeeded : current.cateringNeeded,
          tombstoneNeeded: response.data.tombstoneSyncedFromSource ? response.data.tombstoneNeeded : current.tombstoneNeeded,
          cateringSyncedFromSource: response.data.cateringSyncedFromSource,
          tombstoneSyncedFromSource: response.data.tombstoneSyncedFromSource,
          quotationSyncedFromSource: response.data.quotationSyncedFromSource,
          invoiceNumber: response.data.paymentSyncedFromSource ? response.data.invoiceNumber : current.invoiceNumber,
          invoiceAmount: response.data.paymentSyncedFromSource ? response.data.invoiceAmount : current.invoiceAmount,
          invoiceGenerated: response.data.paymentSyncedFromSource ? response.data.invoiceGenerated : current.invoiceGenerated,
          paymentConfirmed: response.data.paymentSyncedFromSource ? response.data.paymentConfirmed : current.paymentConfirmed,
          proofOfPaymentReference: response.data.paymentSyncedFromSource ? response.data.proofOfPaymentReference : current.proofOfPaymentReference,
          paymentSyncedFromSource: response.data.paymentSyncedFromSource,
        } : current);
      }
    }, 30000);
    return () => window.clearInterval(timer);
  }, [selectedPlan?.caseId]);

  const applyFilters = () => {
    setPage(0);
    setSelectedPlan(null);
    loadPlans(0);
  };

  const filterByStatus = async (status: string) => {
    const nextStatus = statusFilter === status ? "" : status;
    setStatusFilter(nextStatus);
    setPage(0);
    setSelectedPlan(null);
    setLoading(true);
    const response: any = await getFuneralPlans({
      status: nextStatus || null,
      searchText: searchText || null,
      page: 0,
      size: pageSize,
    });
    setLoading(false);
    if (response?.status === 200) {
      setPlans(response.data?.plans || []);
      setCounts(response.data?.statusCounts || {});
      setTotalCount(response.data?.totalCount || 0);
    } else {
      showErrorToast(response?.data?.message || "Failed to load funeral plans.");
    }
  };

  const changePage = (_event: React.ChangeEvent<unknown>, nextPage: number) => {
    const zeroBasedPage = nextPage - 1;
    setPage(zeroBasedPage);
    setSelectedPlan(null);
    loadPlans(zeroBasedPage);
  };

  const updateField = (field: string, value: any) => {
    setSelectedPlan((prev: any) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    ...selectedPlan,
    plannerUserId: userId || selectedPlan?.plannerUserId,
    plannerName: selectedPlan?.plannerName || performedBy,
    performedBy,
    roleAtTime: effectiveRole,
    funeralCategory: selectedPlan?.funeralCategory || null,
    bodyLocationType: selectedPlan?.bodyLocationType || null,
    repatriationType: selectedPlan?.repatriationType || "NONE",
    gcooApprovalStatus: selectedPlan?.gcooApprovalStatus || "NOT_REQUIRED",
    quotationAmount: selectedPlan?.quotationAmount === "" ? null : Number(selectedPlan?.quotationAmount || 0),
    invoiceAmount: selectedPlan?.invoiceAmount === "" ? null : Number(selectedPlan?.invoiceAmount || 0),
    feedbackDueDate: selectedPlan?.feedbackDueDate || (selectedPlan?.funeralDate ? dayjs(selectedPlan.funeralDate).add(1, "day").format("YYYY-MM-DD") : null),
  });

  const savePlan = async (showToast = true) => {
    if (!selectedPlan?.caseId) return null;
    if (selectedPlan?.status === "CLAIM_DECLINED") {
      showErrorToast("RMA declined this claim. Funeral planning actions are blocked.");
      return null;
    }
    setSaving(true);
    const response: any = await saveFuneralPlanning(selectedPlan.caseId, buildPayload());
    setSaving(false);
    if (response?.status === 200) {
      setSelectedPlan({ ...defaults, ...response.data });
      if (showToast) {
        showSuccessToast("Draft saved.");
      }
      loadPlans(page);
      return response.data;
    } else {
      showErrorToast(response?.data?.message || "Failed to save funeral plan.");
      return null;
    }
  };

  const validateActiveStep = () => {
    if (!activeWorkstream) return false;
    if (activeWorkstream.deferred) {
      showErrorToast(activeWorkstream.deferredReason || "This step is not available yet.");
      return false;
    }
    if (activeWorkstream.done) return true;
    const missing = (activeWorkstream.requirements || [])
      .filter((requirement: any) => !requirement.done)
      .map((requirement: any) => requirement.label);
    showErrorToast(`Complete SOP ${activeWorkstream.sop}: ${missing.join(", ") || activeWorkstream.title}.`);
    return false;
  };

  const markStepComplete = async () => {
    if (!validateActiveStep()) return;
    const saved = await savePlan(false);
    if (!saved) return;
    if (activeWorkstream.id === "step10") {
      showSuccessToast("SOP 10 complete. Confirm Funeral below to start the operational handoffs.");
      return;
    }
    const currentIndex = planningSteps.findIndex((step: any) => step.id === activeWorkstream.id);
    const nextStep = planningSteps[currentIndex + 1];
    if (nextStep) {
      setSelectedWorkstream(nextStep.id);
      showSuccessToast(`Step ${activeWorkstream.sop} complete. Continue to Step ${nextStep.sop}.`);
    } else {
      showSuccessToast("All SOP steps are complete.");
    }
  };

  const generateHandoverChecklist = async () => {
    if (!selectedPlan?.caseId || !selectedPlan?.id) return;
    if (selectedPlan?.status === "CLAIM_DECLINED") {
      showErrorToast("RMA declined this claim. Handover generation is blocked.");
      return;
    }
    setSaving(true);
    const payload = {
      ...buildPayload(),
      handoverChecklistPrepared: true,
      materialChecklistPrepared: true,
      planningImplementationNotes: selectedPlan.planningImplementationNotes || "Handover and material checklist generated from funeral planning SOP.",
    };
    const response: any = await saveFuneralPlanning(selectedPlan.caseId, payload);
    if (response?.status === 200) {
      const handoverResponse: any = await saveFuneralHandoverChecklist(selectedPlan.id, {
        undertakerName: selectedPlan.undertakerName || "",
        funeralDate: selectedPlan.funeralDate || "",
        deceasedSurname: selectedPlan.familyRepresentativeName || "",
        deceasedNames: selectedPlan.caseNumber || "",
        schemeOrCash: labelize(selectedPlan.funeralCategory || ""),
        policyOrInvoiceNumber: selectedPlan.policyNumber || selectedPlan.invoiceNumber || selectedPlan.purchaseOrderNumber || "",
        familyContact: selectedPlan.familyRepresentativeContact || "",
        packageDetails: selectedPlan.packageDetails || "",
        familyCars: Number(selectedPlan.familyVehicleCount || 0) > 0
          ? `${selectedPlan.familyVehicleCount} required`
          : "Not required",
        serviceVenue: selectedPlan.funeralVenue || "",
        cemeteryName: selectedPlan.burialOrCemeteryDetails || "",
        decorColours: selectedPlan.decorColours || "",
        extrasComments: selectedPlan.additionalItems || "",
        cateringCashOrScheme: selectedPlan.cateringNeeded ? "Required" : "Not required",
        tombstoneCashOrScheme: selectedPlan.tombstoneNeeded ? "Required" : "Not required",
        fleetConfirmed: false,
        performedBy: actor.performedBy,
      });
      if (![200, 201].includes(Number(handoverResponse?.status))) {
        showErrorToast(handoverResponse?.data?.message || "Plan saved, but handover checklist draft could not be created.");
      }
    }
    setSaving(false);
    if (response?.status === 200) {
      setSelectedPlan({ ...defaults, ...response.data });
      showSuccessToast("Handover checklist generated.");
      loadPlans(page);
    } else {
      showErrorToast(response?.data?.message || "Failed to generate handover checklist.");
    }
  };

  const requestPurchaseOrder = async () => {
    if (!selectedPlan?.caseId || !selectedPlan?.id) return;
    if (!selectedPlan.purchaseOrderRequired) {
      showErrorToast("Mark PO required before creating a purchase order request.");
      return;
    }
    const saved = await savePlan(false);
    if (!saved?.id) return;
    setSaving(true);
    const response: any = await requestFuneralPurchaseOrder(saved.id, {
      requestedBy: performedBy,
      supplierName: selectedPlan.subcontractorName || selectedPlan.undertakerName || "",
      purchaseOrderNumber: selectedPlan.purchaseOrderNumber || "",
      amount: selectedPlan.invoiceAmount === "" ? null : Number(selectedPlan.invoiceAmount || 0),
      reason: selectedPlan.additionalItems || selectedPlan.clientRequirements || "Funeral planning purchase order",
      notes: selectedPlan.purchaseOrderStatus || "",
    });
    setSaving(false);
    if ([200, 201].includes(Number(response?.status))) {
      setSelectedPlan({
        ...defaults,
        ...saved,
        purchaseOrderRequested: true,
        purchaseOrderRequestedBy: response.data?.requestedBy || performedBy,
        purchaseOrderRequestedAt: response.data?.requestedAt,
        purchaseOrderStatus: response.data?.status || "REQUESTED",
        purchaseOrderNumber: response.data?.purchaseOrderNumber || response.data?.poRequestNumber || selectedPlan.purchaseOrderNumber,
      });
      showSuccessToast(`PO request ${response.data?.poRequestNumber || ""} created.`);
      loadPlans(page);
    } else {
      showErrorToast(response?.data?.message || "Failed to create purchase order request.");
    }
  };

  const submitPlan = async () => {
    if (!confirmChecklist.canConfirm) {
      showErrorToast(`Complete before confirming: ${confirmChecklist.missing.join(", ")}.`);
      return;
    }
    const saved = await savePlan(false);
    if (!saved) return;
    await runAction("confirm");
  };

  const completeCase = async () => {
    if (!canCompleteCase) {
      showErrorToast("Capture post-funeral feedback notes before completing the case.");
      return;
    }
    const saved = await savePlan(false);
    if (!saved) return;
    await runAction("complete");
  };

  const runAction = async (action: "ready" | "confirm" | "feedback" | "complete") => {
    if (!selectedPlan?.id) return;
    if (selectedPlan?.status === "CLAIM_DECLINED") {
      showErrorToast("RMA declined this claim. Funeral planning actions are blocked.");
      return;
    }
    setSaving(true);
    const response: any =
      action === "ready"
        ? await markFuneralPlanningReady(selectedPlan.id, actor)
        : action === "confirm"
          ? await confirmFuneralPlanning(selectedPlan.id, actor)
          : action === "feedback"
            ? await triggerFuneralPlanningFeedback(selectedPlan.id, actor)
            : await completeFuneralPlanning(selectedPlan.id, actor);
    setSaving(false);
    if (response?.status === 200) {
      showSuccessToast(response.data?.message || "Funeral plan updated.");
      await openPlan(selectedPlan, false);
      loadPlans(page);
    } else {
      showErrorToast(response?.data?.message || "Action failed.");
    }
  };

  const updateTask = async (taskType: string, action: "request" | "complete") => {
    if (!selectedPlan?.id) return;
    if (selectedPlan?.status === "CLAIM_DECLINED") {
      showErrorToast("RMA declined this claim. Department tasks cannot be processed.");
      return;
    }
    setSaving(true);
    const taskPayload = {
      ...actor,
      assignedTo: taskTeamMap[taskType],
      notes: action === "request"
        ? `Planner requested ${labelize(taskType)} from ${labelize(taskTeamMap[taskType] || "")}`
        : `Planner marked ${labelize(taskType)} coordination complete`,
    };
    const response: any =
      action === "request"
        ? await requestFuneralPlanningTask(selectedPlan.id, taskType, taskPayload)
        : await completeFuneralPlanningTask(selectedPlan.id, taskType, taskPayload);
    setSaving(false);
    if (response?.status === 200) {
      setSelectedPlan({ ...defaults, ...response.data });
      showSuccessToast(action === "request" ? `${labelize(taskType)} request sent to ${labelize(taskPayload.assignedTo)}.` : "Task completed.");
      loadPlans(page);
    } else {
      showErrorToast(response?.data?.message || "Task update failed.");
    }
  };

  const followUpTask = async (task: any) => {
    if (!selectedPlan?.id || !task?.taskType) return;
    setSaving(true);
    const response: any = await followUpFuneralPlanningTask(selectedPlan.id, task.taskType, {
      ...actor,
      assignedTo: task.assignedTo || taskTeamMap[task.taskType],
      referenceNumber: task.referenceNumber,
      notes: `Please update delayed/pending ${labelize(task.taskType)} work for funeral ${selectedPlan.caseNumber}.`,
    });
    setSaving(false);
    if (response?.status === 200) {
      setSelectedPlan({ ...defaults, ...response.data });
      showSuccessToast(`Follow-up sent to ${labelize(task.assignedTo || taskTeamMap[task.taskType] || "team")}.`);
    } else {
      showErrorToast(response?.data?.message || "Follow-up could not be sent.");
    }
  };

  const planningSteps = useMemo(() => {
    if (!selectedPlan) return [];
    const confirmedStatuses = ["CONFIRMED", "IN_EXECUTION", "FEEDBACK_PENDING", "COMPLETED", "CLOSED"];
    const funeralConfirmed = confirmedStatuses.includes(String(selectedPlan.status || ""));
    const feedbackDueDate = selectedPlan.feedbackDueDate
      || (selectedPlan.funeralDate ? dayjs(selectedPlan.funeralDate).add(1, "day").format("YYYY-MM-DD") : "");
    const feedbackWindowOpen = funeralConfirmed
      && hasText(feedbackDueDate)
      && !dayjs().startOf("day").isBefore(dayjs(feedbackDueDate).startOf("day"));
    const paymentRequired =
      selectedPlan.paymentConfirmationRequired ||
      ["CASH", "SCHEME_TOP_UP"].includes(String(selectedPlan.funeralCategory || ""));
    const category = String(selectedPlan.funeralCategory || "");
    const categoryRequirementsReady = category === "SUBCONTRACTED"
      ? hasText(selectedPlan.subcontractorName)
        && hasText(selectedPlan.subcontractorContact)
        && hasText(selectedPlan.subcontractingReason)
        && selectedPlan.gcooApprovalStatus === "APPROVED"
        && Boolean(selectedPlan.familyRepresentativeAcceptedSubcontractor)
        && Boolean(selectedPlan.subcontractorReputationConfirmed)
        && Boolean(selectedPlan.principalPackageUnderstood)
      : category === "MASS"
        ? Number(selectedPlan.massFuneralFamilyCount || 0) >= 3
          && hasText(selectedPlan.massFuneralPackageConsistency)
        : category === "STATE"
          ? hasText(selectedPlan.stateGovernmentSphere)
            && hasText(selectedPlan.stateSafetyRequirements)
            && Number(selectedPlan.stateExpectedAttendees || 0) > 0
            && hasText(selectedPlan.stateProtocolOwner)
            && hasText(selectedPlan.stateVenueType)
            && hasText(selectedPlan.stateParkingOwner)
          : true;
    const steps = [
      {
        id: "step1",
        sop: "1",
        title: "Client Contact",
        icon: <CallOutlinedIcon />,
        done: hasText(selectedPlan.internalClientName || selectedPlan.plannerName) && hasText(selectedPlan.familyRepresentativeName) && hasText(selectedPlan.familyRepresentativeContact),
        note: "Confirm the internal Icebolethu contact and the external family representative.",
      },
      {
        id: "step2",
        sop: "2",
        title: "Requirements",
        icon: <ChecklistOutlinedIcon />,
        done: hasText(selectedPlan.clientRequirements),
        note: "Capture family requirements, preferences, special instructions and expectations.",
      },
      {
        id: "step3",
        sop: "3",
        title: "Body Location Verification",
        icon: <PlaceOutlinedIcon />,
        done: hasText(selectedPlan.bodyLocationType) && (hasText(selectedPlan.bodyLocationName) || hasText(selectedPlan.bodyLocationAddress) || hasText(selectedPlan.icebolethuMortuaryName)),
        note: "Review the body location already captured on the case/body removal record before planning removal or preparation.",
      },
      {
        id: "step4",
        sop: "4",
        title: "Removal Coordination",
        icon: <LocalShippingOutlinedIcon />,
        done: hasText(selectedPlan.bodyLocationType) && (!selectedPlan.bodyRemovalRequired || selectedPlan.bodyRemovalAuthorized),
        note: "Confirm the correct removal path and authorization. Driver, vehicle, mileage and dispatch execution stays in Body Removal/Fleet.",
      },
      {
        id: "step5",
        sop: "5",
        title: "BI1663",
        icon: <DescriptionOutlinedIcon />,
        done: isProgressDoc(selectedPlan.bi1663Status) && selectedPlan.doctorSignedBi1663 && selectedPlan.bi1663AllPagesCompleted,
        note: "Check all 4 BI1663 pages and required doctor signature.",
      },
      {
        id: "step6",
        sop: "6",
        title: "Registration Docs",
        icon: <FactCheckOutlinedIcon />,
        done: isProgressDoc(selectedPlan.deathCertificateStatus) && selectedPlan.deceasedIdReceived && selectedPlan.informantIdReceived && (selectedPlan.repatriationType === "NONE" || selectedPlan.exportPermitReceived),
        note: "Track death certificate, ID documents and repatriation permits if required.",
      },
      {
        id: "step7",
        sop: "7",
        title: "Funeral Category",
        icon: <AssignmentTurnedInOutlinedIcon />,
        done: hasText(selectedPlan.funeralCategory)
          && hasText(selectedPlan.packageDetails)
          && categoryRequirementsReady,
        note: "Select Scheme, Scheme Top-Up, Cash, Mass, State or Subcontracted and confirm package scope.",
      },
      {
        id: "step8",
        sop: "8",
        title: "Quotation",
        icon: <PaidOutlinedIcon />,
        done: Boolean(selectedPlan.quotationAccepted),
        note: "Prepare a formal system quotation and capture family/state acceptance.",
      },
      {
        id: "step9",
        sop: "9",
        title: "Invoice & Payment",
        icon: <PaidOutlinedIcon />,
        done: (!paymentRequired && selectedPlan.funeralCategory !== "STATE") || (selectedPlan.invoiceGenerated && (selectedPlan.paymentConfirmed || hasText(selectedPlan.purchaseOrderNumber))),
        note: "Issue invoice and confirm payment or official purchase order before preparation.",
      },
      {
        id: "step10",
        sop: "10",
        title: "Implementation",
        icon: <Inventory2OutlinedIcon />,
        done: hasText(selectedPlan.funeralDate)
          && hasText(selectedPlan.funeralVenue)
          && hasText(selectedPlan.burialOrCemeteryDetails)
          && selectedPlan.handoverChecklistPrepared
          && selectedPlan.materialChecklistPrepared,
        note: "Coordinate body prep, tents, decor, fleet, warehouse, undertaker and handover checklist.",
      },
      {
        id: "step11",
        sop: "11",
        title: "After Funeral Feedback",
        icon: <FeedbackOutlinedIcon />,
        done: hasText(selectedPlan.feedbackNotes),
        deferred: !feedbackWindowOpen,
        deferredReason: !funeralConfirmed
          ? "Confirm the funeral first. Feedback becomes available only after the funeral."
          : `Feedback is scheduled for ${feedbackDueDate ? dayjs(feedbackDueDate).format("DD MMM YYYY") : "the day after the funeral"}.`,
        note: !funeralConfirmed
          ? "Post-funeral step. It does not block Funeral Confirmation."
          : feedbackWindowOpen
            ? "Capture the family's post-funeral feedback to close the planning cycle."
            : `Scheduled for ${feedbackDueDate ? dayjs(feedbackDueDate).format("DD MMM YYYY") : "the day after the funeral"}.`,
      },
    ];
    const requirements: Record<string, { label: string; done: boolean }[]> = {
      step1: [
        { label: "Planner or internal contact", done: hasText(selectedPlan.internalClientName || selectedPlan.plannerName) },
        { label: "Family representative", done: hasText(selectedPlan.familyRepresentativeName) },
        { label: "Family representative contact", done: hasText(selectedPlan.familyRepresentativeContact) },
      ],
      step2: [{ label: "Family requirements and preferences", done: hasText(selectedPlan.clientRequirements) }],
      step3: [
        { label: "Body location type", done: hasText(selectedPlan.bodyLocationType) },
        { label: "Location name, address or mortuary", done: hasText(selectedPlan.bodyLocationName) || hasText(selectedPlan.bodyLocationAddress) || hasText(selectedPlan.icebolethuMortuaryName) },
      ],
      step4: [
        { label: "Body location type", done: hasText(selectedPlan.bodyLocationType) },
        { label: "Removal authorization, if removal is required", done: !selectedPlan.bodyRemovalRequired || Boolean(selectedPlan.bodyRemovalAuthorized) },
      ],
      step5: [
        { label: "BI1663 status", done: isProgressDoc(selectedPlan.bi1663Status) },
        { label: "Doctor signature", done: Boolean(selectedPlan.doctorSignedBi1663) },
        { label: "All BI1663 pages complete", done: Boolean(selectedPlan.bi1663AllPagesCompleted) },
      ],
      step6: [
        { label: "Death certificate status", done: isProgressDoc(selectedPlan.deathCertificateStatus) },
        { label: "Deceased ID", done: Boolean(selectedPlan.deceasedIdReceived) },
        { label: "Informant ID", done: Boolean(selectedPlan.informantIdReceived) },
        { label: "Export permit, if repatriation applies", done: selectedPlan.repatriationType === "NONE" || Boolean(selectedPlan.exportPermitReceived) },
      ],
      step7: [
        { label: "Funeral category", done: hasText(selectedPlan.funeralCategory) },
        { label: "Package details", done: hasText(selectedPlan.packageDetails) },
        ...(category === "SUBCONTRACTED" ? [
          { label: "Subcontractor name and contact", done: hasText(selectedPlan.subcontractorName) && hasText(selectedPlan.subcontractorContact) },
          { label: "Subcontracting reason", done: hasText(selectedPlan.subcontractingReason) },
          { label: "GCOO approval and family acceptance", done: selectedPlan.gcooApprovalStatus === "APPROVED" && Boolean(selectedPlan.familyRepresentativeAcceptedSubcontractor) },
          { label: "Reputation and package checks", done: Boolean(selectedPlan.subcontractorReputationConfirmed) && Boolean(selectedPlan.principalPackageUnderstood) },
        ] : category === "MASS" ? [
          { label: "At least three families", done: Number(selectedPlan.massFuneralFamilyCount || 0) >= 3 },
          { label: "Consistent mass funeral package", done: hasText(selectedPlan.massFuneralPackageConsistency) },
        ] : category === "STATE" ? [
          { label: "Government sphere and safety requirements", done: hasText(selectedPlan.stateGovernmentSphere) && hasText(selectedPlan.stateSafetyRequirements) },
          { label: "Expected attendees and protocol owner", done: Number(selectedPlan.stateExpectedAttendees || 0) > 0 && hasText(selectedPlan.stateProtocolOwner) },
          { label: "Venue type and parking owner", done: hasText(selectedPlan.stateVenueType) && hasText(selectedPlan.stateParkingOwner) },
        ] : []),
      ],
      step8: [{ label: "Quotation accepted", done: Boolean(selectedPlan.quotationAccepted) }],
      step9: !paymentRequired && category !== "STATE" ? [
        { label: "No payment confirmation required for this category", done: true },
      ] : [
        { label: "Invoice generated", done: Boolean(selectedPlan.invoiceGenerated) },
        { label: "Payment confirmed or purchase order captured", done: Boolean(selectedPlan.paymentConfirmed) || hasText(selectedPlan.purchaseOrderNumber) },
      ],
      step10: [
        { label: "Funeral date", done: hasText(selectedPlan.funeralDate) },
        { label: "Funeral venue", done: hasText(selectedPlan.funeralVenue) },
        { label: "Burial or cemetery details", done: hasText(selectedPlan.burialOrCemeteryDetails) },
        { label: "Handover checklist", done: Boolean(selectedPlan.handoverChecklistPrepared) },
        { label: "Material checklist", done: Boolean(selectedPlan.materialChecklistPrepared) },
      ],
      step11: [{ label: "Post-funeral feedback notes", done: hasText(selectedPlan.feedbackNotes) }],
    };
    return steps.map((step, index) => ({
      ...step,
      requirements: requirements[step.id] || [],
      sequence: index + 1,
      blocked: !step.done && !step.deferred,
    }));
  }, [selectedPlan]);

  const readiness = useMemo(() => {
    const preFuneralSteps = planningSteps.filter((item: any) => item.id !== "step11");
    const total = preFuneralSteps.length || 1;
    const done = preFuneralSteps.filter((item: any) => item.done).length;
    const blocked = preFuneralSteps.filter((item: any) => item.blocked).length;
    return { total, done, blocked, percent: Math.round((done / total) * 100) };
  }, [planningSteps]);

  const blockers = planningSteps.filter((item: any) => item.id !== "step11" && item.blocked);
  const activeWorkstream = planningSteps.find((item: any) => item.id === selectedWorkstream) || planningSteps[0];
  const confirmChecklist = useMemo(() => {
    if (!selectedPlan) {
      return { canConfirm: false, missing: ["Funeral plan"] };
    }
    const paymentRequired = ["CASH", "SCHEME_TOP_UP"].includes(String(selectedPlan.funeralCategory || ""));
    const checks = [
      ...planningSteps.filter((step: any) => step.id !== "step11")
        .map((step: any) => ({ label: `Complete SOP ${step.sop}: ${step.title}`, done: Boolean(step.done) })),
      { label: "Select funeral category", done: hasText(selectedPlan.funeralCategory) },
      { label: "Accept quotation", done: Boolean(selectedPlan.quotationAccepted) },
      { label: "Confirm payment", done: !paymentRequired || Boolean(selectedPlan.paymentConfirmed) },
      { label: "Capture funeral date", done: hasText(selectedPlan.funeralDate) },
      { label: "Capture funeral venue", done: hasText(selectedPlan.funeralVenue) },
      { label: "Capture burial / cemetery details", done: hasText(selectedPlan.burialOrCemeteryDetails) },
      { label: "Approve subcontracting requirements", done: selectedPlan.funeralCategory !== "SUBCONTRACTED" || (hasText(selectedPlan.subcontractorName) && hasText(selectedPlan.subcontractorContact) && hasText(selectedPlan.subcontractingReason) && selectedPlan.gcooApprovalStatus === "APPROVED" && selectedPlan.familyRepresentativeAcceptedSubcontractor && selectedPlan.subcontractorReputationConfirmed && selectedPlan.principalPackageUnderstood) },
      { label: "Complete mass funeral details", done: selectedPlan.funeralCategory !== "MASS" || (Number(selectedPlan.massFuneralFamilyCount || 0) >= 3 && hasText(selectedPlan.massFuneralPackageConsistency)) },
      { label: "Complete state funeral details", done: selectedPlan.funeralCategory !== "STATE" || (hasText(selectedPlan.stateGovernmentSphere) && hasText(selectedPlan.stateSafetyRequirements) && Number(selectedPlan.stateExpectedAttendees || 0) > 0 && hasText(selectedPlan.stateProtocolOwner) && hasText(selectedPlan.stateVenueType) && hasText(selectedPlan.stateParkingOwner)) },
      { label: "Request or capture purchase order", done: selectedPlan.funeralCategory !== "STATE" || !selectedPlan.purchaseOrderRequired || Boolean(selectedPlan.purchaseOrderRequested || hasText(selectedPlan.purchaseOrderNumber)) },
    ];
    return {
      canConfirm: checks.every((item) => item.done),
      missing: checks.filter((item) => !item.done).map((item) => item.label),
    };
  }, [selectedPlan, planningSteps]);
  const canCompleteCase = useMemo(() => {
    if (!selectedPlan) return false;
    const feedbackAvailableOn = selectedPlan.feedbackDueDate
      || (selectedPlan.funeralDate ? dayjs(selectedPlan.funeralDate).add(1, "day").format("YYYY-MM-DD") : "");
    return ["CONFIRMED", "IN_EXECUTION", "FEEDBACK_PENDING", "COMPLETED"].includes(String(selectedPlan.status || ""))
      && hasText(feedbackAvailableOn)
      && !dayjs().startOf("day").isBefore(dayjs(feedbackAvailableOn).startOf("day"))
      && hasText(selectedPlan.feedbackNotes);
  }, [selectedPlan]);
  const isClaimDeclined = selectedPlan?.status === "CLAIM_DECLINED";

  const renderWorkstream = () => {
    if (!selectedPlan || !activeWorkstream) return null;
    switch (activeWorkstream.id) {
      case "step1":
        return (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.5 }}>
            <SectionCard title="Internal Contact (Icebolethu)" icon={<HomeOutlinedIcon />}>
              <Cell><Field label="Internal Source / Branch / Department" value={selectedPlan.internalClientName || ""} onChange={(e: any) => updateField("internalClientName", e.target.value)} helperText="Example: Icebolethu branch, call centre, planner, mortuary, employee." /></Cell>
              <Cell><Field label="Assigned Funeral Planner" value={selectedPlan.plannerName || ""} onChange={(e: any) => updateField("plannerName", e.target.value)} /></Cell>
              <Cell><Field label="Policy / Case Reference" value={selectedPlan.policyNumber || selectedPlan.caseNumber || ""} onChange={(e: any) => updateField("policyNumber", e.target.value)} helperText="Reference only; policy/case values may come from the case record." /></Cell>
            </SectionCard>
            <SectionCard title="External Contact (Family Representative)" icon={<PersonOutlineOutlinedIcon />}>
              <Cell><Field label="Representative Name" value={selectedPlan.familyRepresentativeName || selectedPlan.externalClientName || ""} onChange={(e: any) => {
                updateField("familyRepresentativeName", e.target.value);
                updateField("externalClientName", e.target.value);
              }} /></Cell>
              <Cell md={6}><Field label="Relationship" value={selectedPlan.representativeRelationship || ""} onChange={(e: any) => updateField("representativeRelationship", e.target.value)} /></Cell>
              <Cell md={6}><Field label="Mobile Number" value={selectedPlan.familyRepresentativeContact || ""} onChange={(e: any) => updateField("familyRepresentativeContact", e.target.value)} /></Cell>
              <Cell><Alert severity="info" sx={{ borderRadius: "8px" }}>If the family representative came from claimant/informant or body removal, it is prefilled here. Planner only verifies or corrects it.</Alert></Cell>
            </SectionCard>
          </Box>
        );
      case "step2":
        return (
          <SectionCard title="Family Requirements" icon={<ChecklistOutlinedIcon />}>
            <Cell><Field multiline minRows={7} label="Family requirements, preferences and internal notes" value={selectedPlan.clientRequirements || ""} onChange={(e: any) => updateField("clientRequirements", e.target.value)} helperText="Capture funeral expectations, decor preference, vehicles, catering, top-ups, venue, cultural or religious notes." /></Cell>
          </SectionCard>
        );
      case "step3":
        return (
          <SectionCard title="Body Location Verification" icon={<PlaceOutlinedIcon />}>
            <Cell>
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                This section verifies the location already captured during case creation or body removal. The planner does not re-capture the same location here; if it is wrong, update the source record so Body Removal, Mortuary and Planning stay aligned.
              </Alert>
            </Cell>
            <Cell md={4}><Field label="Body Location Type" value={labelize(selectedPlan.bodyLocationType || "Pending")} disabled helperText="Pulled from case/body removal." /></Cell>
            <Cell md={4}><Field label="Location Name" value={selectedPlan.bodyLocationName || selectedPlan.icebolethuMortuaryName || "Pending"} disabled helperText="Hospital, home, undertaker or mortuary name." /></Cell>
            <Cell md={4}><Field label="Icebolethu Mortuary" value={selectedPlan.icebolethuMortuaryName || "Not applicable"} disabled helperText="Only applies once the body is at an Icebolethu mortuary." /></Cell>
            <Cell><Field multiline minRows={3} label="Body Location Address" value={selectedPlan.bodyLocationAddress || "Pending"} disabled /></Cell>
            <Cell>
              <Alert severity={selectedPlan.bodyLocationType ? "success" : "warning"} sx={{ borderRadius: "8px" }}>
                {selectedPlan.bodyLocationType
                  ? "Location is available for planning. Use Step 4 to confirm whether collection/removal coordination is required."
                  : "Body location is still missing on the source record. Capture it in Case Details or Body Removal before planning removal and preparation."}
              </Alert>
            </Cell>
          </SectionCard>
        );
      case "step4":
        return (
          <SectionCard title="Body Removal Coordination" icon={<LocalShippingOutlinedIcon />}>
            <Cell>
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                Planner confirms the operational route. Home/hospital/government mortuary/other undertaker usually needs collection coordination. If the body is already in Icebolethu mortuary, removal may be marked not required. Actual dispatch, driver, vehicle and mileage remain in Body Removal/Fleet.
              </Alert>
            </Cell>
            <Cell md={4}><Field label="Current Body Location" value={labelize(selectedPlan.bodyLocationType || "Pending")} disabled /></Cell>
            <Cell md={4}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="bodyRemovalRequired" label="Collection/removal required" /></Cell>
            <Cell md={4}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="bodyRemovalAuthorized" label="Family/parlor collection authority received" /></Cell>
            <Cell>
              <Alert severity="warning" sx={{ borderRadius: "8px" }}>
                Government mortuary and unnatural-death documentation such as SAPS case number, postmortem and inspector report belong under Step 6 with death registration/repatriation documents.
              </Alert>
            </Cell>
            <Cell><Field multiline minRows={3} label="Removal coordination notes" value={selectedPlan.bodyRemovalAuthorizationNotes || ""} onChange={(e: any) => updateField("bodyRemovalAuthorizationNotes", e.target.value)} helperText="Record who approved collection, where collection is requested from, and any handover constraints. Do not enter driver/vehicle/mileage here." /></Cell>
          </SectionCard>
        );
      case "step5":
        return (
          <SectionCard title="BI1663 Notice of Death" icon={<DescriptionOutlinedIcon />}>
            <Cell md={4}><SelectField selectedPlan={selectedPlan} updateField={updateField} field="bi1663Status" label="BI1663 Status" options={documentStatuses} /></Cell>
            <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="doctorSignedBi1663" label="Doctor signed BI1663" /></Cell>
            <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="bi1663AllPagesCompleted" label="All BI1663 pages complete" /></Cell>
            <Cell><Alert severity="warning" sx={{ borderRadius: "8px" }}>Check all four pages and the correct certifying doctor/signature before moving to registration.</Alert></Cell>
          </SectionCard>
        );
      case "step6":
        return (
          <SectionCard title="Death Registration & Repatriation" icon={<FactCheckOutlinedIcon />}>
            <Cell>
              <Alert severity="info" sx={{ borderRadius: "8px" }}>
                Use this step to track Home Affairs documents, unnatural-death documents and repatriation permits. These are planning checks only; actual document files remain in Documents/Mortuary where applicable.
              </Alert>
            </Cell>
            <Cell md={6}>
              <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", p: 1.5, bgcolor: "#FBFDFF", height: "100%" }}>
                <Stack direction="row" alignItems="center" gap={1} mb={1.25}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: "#EFF6FF", color: blue }}><DescriptionOutlinedIcon fontSize="small" /></Box>
                  <Box>
                    <Typography fontWeight={950} fontSize={14}>Death Registration Documents</Typography>
                    <Typography color={muted} fontSize={12}>BI1663, deceased ID/birth certificate, informant ID and death certificate status.</Typography>
                  </Box>
                </Stack>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 1.25 }}>
                  <Cell md={6}><SelectField selectedPlan={selectedPlan} updateField={updateField} field="deathCertificateStatus" label="Death Certificate Status" options={documentStatuses} /></Cell>
                  <Cell md={6}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="deathRegistered" label="Death registered at Home Affairs" /></Cell>
                  <Cell md={6}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="deceasedIdReceived" label="Deceased ID / birth certificate received" /></Cell>
                  <Cell md={6}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="informantIdReceived" label="Informant ID received" /></Cell>
                  <Cell><Toggle selectedPlan={selectedPlan} updateField={updateField} field="statutoryDocumentsReady" label="Statutory documents ready for funeral file" /></Cell>
                </Box>
              </Paper>
            </Cell>
            <Cell md={6}>
              <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", p: 1.5, bgcolor: "#FFFDF8", height: "100%" }}>
                <Stack direction="row" alignItems="center" gap={1} mb={1.25}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: orangeSoft, color: orange }}><FactCheckOutlinedIcon fontSize="small" /></Box>
                  <Box>
                    <Typography fontWeight={950} fontSize={14}>Unnatural / Government Mortuary Checks</Typography>
                    <Typography color={muted} fontSize={12}>Required when death is unnatural or the body is with government mortuary/SAPS process.</Typography>
                  </Box>
                </Stack>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 1.25 }}>
                  <Cell md={4}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="caseNumberOpened" label="SAPS case number available" /></Cell>
                  <Cell md={4}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="postMortemConducted" label="Postmortem conducted" /></Cell>
                  <Cell md={4}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="inspectorReportSigned" label="Inspector report signed" /></Cell>
                  <Cell>
                    <Alert severity="warning" sx={{ borderRadius: "8px", py: 0.5 }}>
                      If this funeral is natural and not from government mortuary, these checks can remain off.
                    </Alert>
                  </Cell>
                </Box>
              </Paper>
            </Cell>
            <Cell>
              <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", p: 1.5, bgcolor: "#FAFAFA" }}>
                <Stack direction="row" alignItems="center" gap={1} mb={1.25}>
                  <Box sx={{ width: 30, height: 30, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: "#F0FDF4", color: green }}><LocalShippingOutlinedIcon fontSize="small" /></Box>
                  <Box>
                    <Typography fontWeight={950} fontSize={14}>Repatriation / Export Readiness</Typography>
                    <Typography color={muted} fontSize={12}>Use for domestic repatriation or cross-border movement before confirming burial date.</Typography>
                  </Box>
                </Stack>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(0, 1fr))", gap: 1.25 }}>
                  <Cell md={4}><SelectField selectedPlan={selectedPlan} updateField={updateField} field="repatriationType" label="Repatriation Type" options={repatriationTypes} /></Cell>
                  <Cell md={4}><Field label="Mode" value={selectedPlan.repatriationMode || ""} onChange={(e: any) => updateField("repatriationMode", e.target.value)} helperText="Road, air, local transfer, cross-border..." /></Cell>
                  <Cell md={4}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="embalmed" label="Body embalmed" /></Cell>
                  <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="embalmingCertificateReceived" label="Embalming certificate" /></Cell>
                  <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="nonInfectiousLetterReceived" label="Non-infectious letter" /></Cell>
                  <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="exportPermitReceived" label="Export permit" /></Cell>
                  <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="clearanceCertificateReceived" label="Clearance certificate" /></Cell>
                </Box>
              </Paper>
            </Cell>
          </SectionCard>
        );
      case "step7":
        return (
          <SectionCard title="Funeral Category, Benefits & Package" icon={<AssignmentTurnedInOutlinedIcon />}>
            <Cell md={3}><SelectField selectedPlan={selectedPlan} updateField={updateField} field="funeralCategory" label="Funeral Category" options={categories} /></Cell>
            <Cell md={3}><Field label="Policy Number" value={selectedPlan.policyNumber || ""} onChange={(e: any) => updateField("policyNumber", e.target.value)} /></Cell>
            <Cell md={3}><Field label="Decor Colours" value={selectedPlan.decorColours || ""} onChange={(e: any) => updateField("decorColours", e.target.value)} /></Cell>
            <Cell md={3}><Field label="Vehicle Colour Preference" value={selectedPlan.vehicleColourPreference || ""} onChange={(e: any) => updateField("vehicleColourPreference", e.target.value)} helperText="Sent to Fleet when the funeral is confirmed." /></Cell>
            <Cell><Field multiline minRows={3} label="Package Details / Policy Benefits" value={selectedPlan.packageDetails || ""} onChange={(e: any) => updateField("packageDetails", e.target.value)} helperText="Auto-populates from package and selected claim benefits where available; editable for scheme/cash cases." /></Cell>
            <Cell><Field multiline minRows={3} label="Additional Items (catering, tombstone, family cars, Inkomo, sound, livestream...)" value={selectedPlan.additionalItems || ""} onChange={(e: any) => updateField("additionalItems", e.target.value)} helperText="Auto-populates selected top-up/service benefits from the claim; planner can add or remove items." /></Cell>
            <Cell md={3}>
              <ServiceRequirementField field="cateringNeeded" label="Catering" selectedPlan={selectedPlan} updateField={updateField} sourceLinked={Boolean(selectedPlan.cateringSyncedFromSource)} />
            </Cell>
            <Cell md={3}>
              <ServiceRequirementField field="tombstoneNeeded" label="Tombstone" selectedPlan={selectedPlan} updateField={updateField} sourceLinked={Boolean(selectedPlan.tombstoneSyncedFromSource)} />
            </Cell>
            <Cell md={3}><Field type="number" label="Hearses Required" value={selectedPlan.hearseVehicleCount ?? 1} onChange={(e: any) => updateField("hearseVehicleCount", Math.min(10, Math.max(1, Number(e.target.value || 1))))} helperText="At least one hearse is required." /></Cell>
            <Cell md={3}><Field type="number" label="Family Cars Required" value={selectedPlan.familyVehicleCount ?? (selectedPlan.familyCarRequired ? 1 : 0)} onChange={(e: any) => {
              const count = Math.min(10, Math.max(0, Number(e.target.value || 0)));
              updateField("familyVehicleCount", count);
              updateField("familyCarRequired", count > 0);
            }} helperText="Each car receives its own driver and vehicle." /></Cell>
            <Cell md={3}><Field type="number" label="Additional Vehicles" value={selectedPlan.additionalVehicleCount ?? 0} onChange={(e: any) => updateField("additionalVehicleCount", Math.min(10, Math.max(0, Number(e.target.value || 0))))} helperText="Use for extra transport beyond hearse/family cars." /></Cell>
            <Cell md={3}><SelectField selectedPlan={selectedPlan} updateField={updateField} field="gcooApprovalStatus" label="GCOO Approval" options={approvalStatuses} /></Cell>
            {selectedPlan.funeralCategory === "SUBCONTRACTED" && (
              <>
                <Cell><Alert severity="warning" sx={{ borderRadius: "8px" }}>Subcontracting requires family acceptance and GCOO approval before funeral confirmation.</Alert></Cell>
                <Cell md={4}><Field label="Subcontractor Name" value={selectedPlan.subcontractorName || ""} onChange={(e: any) => updateField("subcontractorName", e.target.value)} /></Cell>
                <Cell md={4}><Field label="Subcontractor Contact" value={selectedPlan.subcontractorContact || ""} onChange={(e: any) => updateField("subcontractorContact", e.target.value)} /></Cell>
                <Cell md={4}><Field label="Subcontractor Quotation Ref" value={selectedPlan.subcontractorQuotationReference || ""} onChange={(e: any) => updateField("subcontractorQuotationReference", e.target.value)} /></Cell>
                <Cell><Field multiline minRows={3} label="Subcontracting Reason" value={selectedPlan.subcontractingReason || ""} onChange={(e: any) => updateField("subcontractingReason", e.target.value)} helperText="Distance, high risk travel, cross-border, language/cultural fit, or other reason." /></Cell>
                <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="subcontractorReputationConfirmed" label="Reputation checked" /></Cell>
                <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="principalPackageUnderstood" label="Principal package understood" /></Cell>
                <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="familyRepresentativeAcceptedSubcontractor" label="Family accepted subcontractor" /></Cell>
                <Cell md={3}><Field label="GCOO Approved By" value={selectedPlan.gcooApprovedBy || ""} onChange={(e: any) => updateField("gcooApprovedBy", e.target.value)} /></Cell>
                <Cell><Field multiline minRows={2} label="GCOO Approval Notes" value={selectedPlan.gcooApprovalNotes || ""} onChange={(e: any) => updateField("gcooApprovalNotes", e.target.value)} /></Cell>
              </>
            )}
            {selectedPlan.funeralCategory === "MASS" && (
              <>
                <Cell><Alert severity="info" sx={{ borderRadius: "8px" }}>Mass funeral planning tracks multiple families, package consistency, venue and added services before handover.</Alert></Cell>
                <Cell md={4}><Field label="Mass Funeral Venue" value={selectedPlan.massFuneralVenue || ""} onChange={(e: any) => updateField("massFuneralVenue", e.target.value)} /></Cell>
                <Cell md={4}><Field type="number" label="Number of Families" value={selectedPlan.massFuneralFamilyCount || ""} onChange={(e: any) => updateField("massFuneralFamilyCount", e.target.value)} /></Cell>
                <Cell md={4}><Field label="Package Consistency" value={selectedPlan.massFuneralPackageConsistency || ""} onChange={(e: any) => updateField("massFuneralPackageConsistency", e.target.value)} helperText="Same package / different packages / mixed benefits." /></Cell>
                <Cell><Field multiline minRows={3} label="Mass Funeral Additional Items" value={selectedPlan.massFuneralAdditionalItems || ""} onChange={(e: any) => updateField("massFuneralAdditionalItems", e.target.value)} /></Cell>
              </>
            )}
            {selectedPlan.funeralCategory === "STATE" && (
              <>
                <Cell><Alert severity="info" sx={{ borderRadius: "8px" }}>State funerals require protocol, safety, venue, dry-run and purchase-order tracking.</Alert></Cell>
                <Cell md={3}><Field label="Government Sphere" value={selectedPlan.stateGovernmentSphere || ""} onChange={(e: any) => updateField("stateGovernmentSphere", e.target.value)} helperText="Municipal / Provincial / National." /></Cell>
                <Cell md={3}><Field type="number" label="Expected Attendees" value={selectedPlan.stateExpectedAttendees || ""} onChange={(e: any) => updateField("stateExpectedAttendees", e.target.value)} /></Cell>
                <Cell md={3}><Field type="date" label="Dry Run Date" value={selectedPlan.stateDryRunDate || ""} onChange={(e: any) => updateField("stateDryRunDate", e.target.value)} /></Cell>
                <Cell md={3}><Field label="Venue Type" value={selectedPlan.stateVenueType || ""} onChange={(e: any) => updateField("stateVenueType", e.target.value)} helperText="Indoor / outdoor." /></Cell>
                <Cell md={4}><Field label="Protocol Owner" value={selectedPlan.stateProtocolOwner || ""} onChange={(e: any) => updateField("stateProtocolOwner", e.target.value)} /></Cell>
                <Cell md={4}><Field label="Parking Owner" value={selectedPlan.stateParkingOwner || ""} onChange={(e: any) => updateField("stateParkingOwner", e.target.value)} /></Cell>
                <Cell md={4}><Field label="Steering Committee" value={selectedPlan.stateSteeringCommittee || ""} onChange={(e: any) => updateField("stateSteeringCommittee", e.target.value)} /></Cell>
                <Cell><Field multiline minRows={3} label="Safety Requirements" value={selectedPlan.stateSafetyRequirements || ""} onChange={(e: any) => updateField("stateSafetyRequirements", e.target.value)} /></Cell>
              </>
            )}
          </SectionCard>
        );
      case "step8":
        return (
          <SectionCard title="Quotation Preparation & Acceptance" icon={<PaidOutlinedIcon />}>
            <Cell><Alert severity={selectedPlan.quotationSyncedFromSource ? "success" : "warning"} sx={{ borderRadius: "8px" }}>{selectedPlan.quotationSyncedFromSource ? "Latest quotation is prefilled from the Quotation module. Planner can amend this planning copy and save it." : "No linked quotation exists yet. Planner fallback capture is available."}</Alert></Cell>
            <Cell md={4}><Field label="Quotation Number" value={selectedPlan.quotationNumber || ""} onChange={(e: any) => updateField("quotationNumber", e.target.value)} /></Cell>
            <Cell md={4}><Field label="Quotation Amount" value={selectedPlan.quotationAmount || ""} onChange={(e: any) => updateField("quotationAmount", e.target.value)} /></Cell>
            <Cell md={2}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="quotationGenerated" label="Generated" /></Cell>
            <Cell md={2}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="quotationAccepted" label="Accepted" /></Cell>
          </SectionCard>
        );
      case "step9":
        return (
          <SectionCard title="Invoice & Payment Confirmation" icon={<PaidOutlinedIcon />}>
            <Cell><Alert severity={selectedPlan.paymentSyncedFromSource ? "success" : "warning"} sx={{ borderRadius: "8px" }}>{selectedPlan.paymentSyncedFromSource ? "Invoice and confirmed payment state are linked from Revenue/Payment and refresh automatically." : "No linked invoice exists yet. Planner fallback capture remains available."}</Alert></Cell>
            <Cell md={4}><Field label="Invoice Number" value={selectedPlan.invoiceNumber || ""} disabled={selectedPlan.paymentSyncedFromSource} onChange={(e: any) => updateField("invoiceNumber", e.target.value)} /></Cell>
            <Cell md={4}><Field label="Invoice Amount" value={selectedPlan.invoiceAmount || ""} disabled={selectedPlan.paymentSyncedFromSource} onChange={(e: any) => updateField("invoiceAmount", e.target.value)} /></Cell>
            <Cell md={2}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="invoiceGenerated" label="Invoice issued" disabled={selectedPlan.paymentSyncedFromSource} /></Cell>
            <Cell md={2}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="paymentConfirmed" label="Payment confirmed" disabled={selectedPlan.paymentSyncedFromSource} /></Cell>
            <Cell md={6}><Field label="Proof of Payment Reference" value={selectedPlan.proofOfPaymentReference || ""} disabled={selectedPlan.paymentSyncedFromSource} onChange={(e: any) => updateField("proofOfPaymentReference", e.target.value)} /></Cell>
            <Cell md={6}><Field label="Purchase Order Number" value={selectedPlan.purchaseOrderNumber || ""} onChange={(e: any) => updateField("purchaseOrderNumber", e.target.value)} /></Cell>
            <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="purchaseOrderRequired" label="PO required" /></Cell>
            <Cell md={3}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="purchaseOrderRequested" label="PO requested" /></Cell>
            <Cell md={3}><Field label="PO Requested By" value={selectedPlan.purchaseOrderRequestedBy || ""} onChange={(e: any) => updateField("purchaseOrderRequestedBy", e.target.value)} /></Cell>
            <Cell md={3}><Field label="PO Status" value={selectedPlan.purchaseOrderStatus || ""} onChange={(e: any) => updateField("purchaseOrderStatus", e.target.value)} helperText="Requested / received / pending state PO." /></Cell>
            <Cell>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Button variant="contained" onClick={requestPurchaseOrder} disabled={saving || !selectedPlan.purchaseOrderRequired || selectedPlan.status === "CLAIM_DECLINED"} sx={orangeButtonSx}>
                  Request Purchase Order
                </Button>
                <Typography variant="caption" color={muted}>
                  Creates a tracked PO request record and updates this plan automatically.
                </Typography>
              </Stack>
            </Cell>
          </SectionCard>
        );
      case "step10":
        return (
          <SectionCard title="Funeral Implementation & Handover" icon={<Inventory2OutlinedIcon />}>
            <Cell md={4}><Field type="date" label="Funeral Date" value={selectedPlan.funeralDate || ""} onChange={(e: any) => {
              updateField("funeralDate", e.target.value);
              if (e.target.value && !selectedPlan.feedbackDueDate) {
                updateField("feedbackDueDate", dayjs(e.target.value).add(1, "day").format("YYYY-MM-DD"));
              }
            }} /></Cell>
            <Cell md={4}><Field label="Funeral Venue" value={selectedPlan.funeralVenue || ""} onChange={(e: any) => updateField("funeralVenue", e.target.value)} /></Cell>
            <Cell md={4}><Field label="Undertaker" value={selectedPlan.undertakerName || ""} onChange={(e: any) => updateField("undertakerName", e.target.value)} /></Cell>
            <Cell><Field multiline minRows={2} label="Burial / Cemetery Details" value={selectedPlan.burialOrCemeteryDetails || ""} onChange={(e: any) => updateField("burialOrCemeteryDetails", e.target.value)} /></Cell>
            <Cell md={6}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="handoverChecklistPrepared" label="Handover checklist prepared" /></Cell>
            <Cell md={6}><Toggle selectedPlan={selectedPlan} updateField={updateField} field="materialChecklistPrepared" label="Material checklist prepared" /></Cell>
            <Cell><Field multiline minRows={4} label="Implementation Notes" value={selectedPlan.planningImplementationNotes || ""} onChange={(e: any) => updateField("planningImplementationNotes", e.target.value)} /></Cell>
          </SectionCard>
        );
      default:
        const feedbackDueDate = selectedPlan.feedbackDueDate
          || (selectedPlan.funeralDate ? dayjs(selectedPlan.funeralDate).add(1, "day").format("YYYY-MM-DD") : "");
        return (
          <SectionCard title="After Funeral Feedback" icon={<FeedbackOutlinedIcon />}>
            {activeWorkstream.deferred && (
              <Cell>
                <Alert severity="info" sx={{ borderRadius: "8px" }}>
                  {activeWorkstream.deferredReason} Warehouse preparation and funeral fleet work start after Funeral Confirmation; this feedback step is not a confirmation dependency.
                </Alert>
              </Cell>
            )}
            <Cell md={4}><Field type="date" label="Feedback Due Date" value={feedbackDueDate} disabled helperText="Automatically scheduled for the day after the funeral" /></Cell>
            <Cell><Field multiline minRows={5} label="Feedback Notes" value={selectedPlan.feedbackNotes || ""} disabled={activeWorkstream.deferred} onChange={(e: any) => updateField("feedbackNotes", e.target.value)} /></Cell>
          </SectionCard>
        );
    }
  };

  const selectedDate = selectedPlan?.funeralDate ? dayjs(selectedPlan.funeralDate).format("DD MMM YYYY") : "Date pending";

  return (
    <Box sx={{ minHeight: "100%", p: { xs: 1.5, md: 2 }, textAlign: "left", background: "linear-gradient(180deg, #FFFFFF 0px, #F7F8FA 118px, #F6F7F9 100%)" }}>
      {/* Standard header — matches OperationsPageShell used across IFAS */}
      <Box sx={{ mb: 2, display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", md: "row" } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "4px minmax(0, 1fr)", columnGap: 1.5, alignItems: "start", maxWidth: 820 }}>
          <Box sx={{ width: 4, height: { xs: 66, md: 76 }, borderRadius: 999, background: `linear-gradient(180deg, ${orange}, #D94F00)`, mt: 0.35 }} />
          <Box minWidth={0}>
            <Chip label="Funeral Planning" size="small" sx={{ bgcolor: orangeSoft, color: orange, fontWeight: 900, borderRadius: 1.5, height: 24, mb: 0.8, "& .MuiChip-label": { px: 1.1 } }} />
            <Typography fontSize={{ xs: 22, md: 28 }} fontWeight={900} color={ink} lineHeight={1.15}>Funeral Planning Workspace</Typography>
            <Typography fontSize={{ xs: 14, md: 15 }} color={muted} lineHeight={1.65} mt={0.5}>Family brief, statutory readiness, finance and operational coordination in one place.</Typography>
          </Box>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ width: { xs: "100%", md: "auto" }, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
          <Field sx={{ width: 240 }} placeholder="Search plan, case, family..." value={searchText} onChange={(e: any) => setSearchText(e.target.value)} />
          <Field select label="Status" value={statusFilter} sx={{ width: 180 }} onChange={(e: any) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {statuses.map((status) => <MenuItem key={status} value={status}>{labelize(status)}</MenuItem>)}
          </Field>
          <Button variant="contained" onClick={applyFilters} disabled={loading} sx={standardPrimaryBtnSx}>Apply</Button>
        </Stack>
      </Box>
      {!selectedPlan && <OperationsSectionHeader title="Pipeline overview" meta="cases by workflow status" />}
      {!selectedPlan && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, gap: 1.25, mb: 2 }}>
          {statuses.slice(0, 10).map((status) => {
            const isActive = statusFilter === status;
            return (
              <Box
                key={status}
                onClick={() => filterByStatus(status)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "13px",
                  transition: "transform 160ms ease, box-shadow 160ms ease",
                  outline: isActive ? `2px solid ${statusColor(status)}` : "none",
                  outlineOffset: isActive ? "1px" : 0,
                  "&:hover": { transform: "translateY(-2px)" },
                }}
              >
                <StandardMetricCard
                  compact
                  label={labelize(status)}
                  value={counts?.[status] || 0}
                  tone={statusTone[status] || "grey"}
                  icon={statusIcons[status]}
                />
              </Box>
            );
          })}
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          border: `1px solid ${border}`,
          borderRadius: "12px",
          mb: 2,
          bgcolor: surface,
          boxShadow: subtleShadow,
        }}
      >
        {claimSearchLoading && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, bgcolor: orangeSoft, "& .MuiLinearProgress-bar": { bgcolor: orange } }} />}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 0.8fr) minmax(420px, 1.2fr)" }, gap: 2, p: { xs: 1.75, md: 2.25 }, alignItems: "center" }}>
          <Stack direction="row" gap={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: orangeSoft, color: orange }}>
              <FactCheckOutlinedIcon />
            </Box>
            <Box>
              <Typography fontWeight={950} color={ink}>Check Claim Status</Typography>
              <Typography color={muted} fontSize={12.5}>Search the Claims System using the policy number.</Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ xs: "stretch", sm: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              label="Policy Number"
              placeholder="e.g. TESTICE009"
              value={claimPolicyNumber}
              disabled={claimSearchLoading}
              inputProps={{ maxLength: 80, "aria-label": "Policy number for claim status search" }}
              onChange={(event) => {
                setClaimPolicyNumber(event.target.value);
                if (claimSearchError) setClaimSearchError("");
                if (claimSearchResults.length) setClaimSearchResults([]);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void searchClaimStatus();
                }
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff" } }}
            />
            <Button
              variant="contained"
              onClick={() => void searchClaimStatus()}
              disabled={claimSearchLoading || !claimPolicyNumber.trim()}
              sx={{ ...standardPrimaryBtnSx, minWidth: 145, whiteSpace: "nowrap" }}
            >
              {claimSearchLoading ? "Checking..." : "Check Status"}
            </Button>
          </Stack>
        </Box>

        {(claimSearchError || claimSearchResults.length > 0) && (
          <Box sx={{ px: { xs: 1.75, md: 2.25 }, pb: 2.25 }}>
            {claimSearchError ? (
              <Alert severity="error" sx={{ borderRadius: "8px", fontWeight: 750 }}>{claimSearchError}</Alert>
            ) : (
              <Stack gap={1}>
                {claimSearchResults.map((claim, index) => {
                  const currentStatus = String(claim?.status || "UNKNOWN");
                  const color = claimStatusColor(currentStatus);
                  return (
                    <Box
                      key={claim?.claim_id || `${claim?.policy_number || claimPolicyNumber}-${index}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(4, minmax(0, 1fr)) auto" },
                        gap: 1.5,
                        alignItems: "center",
                        border: `1px solid ${color}33`,
                        borderLeft: `4px solid ${color}`,
                        borderRadius: "8px",
                        bgcolor: `${color}08`,
                        p: 1.5,
                      }}
                    >
                      <Box>
                        <Typography fontSize={10.5} color={muted} fontWeight={850} textTransform="uppercase">Policy Number</Typography>
                        <Typography fontSize={13.5} color={ink} fontWeight={900}>{claim?.policy_number || claimPolicyNumber.trim()}</Typography>
                      </Box>
                      <Box>
                        <Typography fontSize={10.5} color={muted} fontWeight={850} textTransform="uppercase">Claim Number</Typography>
                        <Typography fontSize={13.5} color={ink} fontWeight={900}>{claim?.ice_claim_number || "Not available"}</Typography>
                      </Box>
                      <Box>
                        <Typography fontSize={10.5} color={muted} fontWeight={850} textTransform="uppercase">Claimant</Typography>
                        <Typography fontSize={13.5} color={ink} fontWeight={900}>{claim?.claimant_name || claim?.main_member_name || "Not available"}</Typography>
                      </Box>
                      <Box>
                        <Typography fontSize={10.5} color={muted} fontWeight={850} textTransform="uppercase">Claim Type</Typography>
                        <Typography fontSize={13.5} color={ink} fontWeight={900}>{labelize(claim?.type_of_claim || "DEATH")}</Typography>
                      </Box>
                      <Chip
                        label={labelize(currentStatus)}
                        sx={{ justifySelf: { xs: "start", sm: "end" }, bgcolor: `${color}14`, color, border: `1px solid ${color}38`, fontWeight: 950 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.5, alignItems: "start" }}>
        <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "12px", bgcolor: surface, overflow: "hidden", display: selectedPlan ? "none" : "block", boxShadow: softShadow }}>
          <Box p={2} borderBottom={`1px solid ${border}`} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography fontWeight={950} fontSize={18}>Assigned Planning Cases</Typography>
              <Typography color={muted} fontSize={13}>Select a case to open its planning workspace.</Typography>
            </Box>
            <Chip size="small" label={`${totalCount} total`} sx={{ fontWeight: 900, bgcolor: orangeSoft, color: orange }} />
          </Box>
          <Stack
            sx={{
              maxHeight: "calc(100vh - 280px)",
              overflowY: "auto",
              overflowX: "hidden",
              p: 1.25,
              gap: 0.85,
              scrollbarWidth: "thin",
              scrollbarColor: "#E2E8F0 transparent",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#E2E8F0", borderRadius: 8 },
              "&::-webkit-scrollbar-thumb:hover": { background: "#CBD5E1" },
              "&::-webkit-scrollbar-button": { display: "none", height: 0, width: 0 },
            }}
          >
            {plans.map((plan) => {
              const active = selectedPlan?.id === plan.id;
              const rowColor = statusColor(plan.status);
              return (
                <Paper
                  elevation={0}
                  key={plan.id}
                  onClick={() => openPlan(plan)}
                  sx={{
                    p: 1.15,
                    pl: 1.45,
                    borderTop: `1px solid ${border}`,
                    borderRight: `1px solid ${border}`,
                    borderBottom: `1px solid ${border}`,
                    borderLeft: `4px solid ${rowColor}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    bgcolor: active ? orangeSoft : "#fff",
                    transition: "box-shadow 160ms ease, border-color 160ms ease",
                    "&:hover": { boxShadow: subtleShadow, borderRightColor: "#CBD5E1", borderTopColor: "#CBD5E1", borderBottomColor: "#CBD5E1" },
                  }}
                >
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 1.25, alignItems: "start" }}>
                    <Stack direction="row" gap={1} minWidth={0} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: "8px",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: `${rowColor}1A`,
                          color: rowColor,
                          fontWeight: 950,
                          fontSize: 11,
                        }}
                      >
                        {getInitials(plan.familyRepresentativeName)}
                      </Box>
                      <Box minWidth={0}>
                        <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap" sx={{ lineHeight: 1 }}>
                          <Typography fontWeight={950} fontSize={14.5} sx={{ lineHeight: 1.15 }}>{plan.caseNumber || plan.planNumber}</Typography>
                          <Chip size="small" label={plan.funeralDate ? dayjs(plan.funeralDate).format("DD MMM YYYY") : "Date pending"} sx={{ height: 20, fontSize: 10.5, bgcolor: "#F1F5F9", color: "#334155", fontWeight: 800 }} />
                          <StatusDotChip status={plan.status} tone={statusTone[plan.status]} />
                        </Stack>
                        <Stack direction="row" gap={0.75} mt={0.65} alignItems="center" flexWrap="wrap">
                          <Typography fontSize={12.5} color={muted} fontWeight={750} sx={{ lineHeight: 1.2, maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {plan.familyRepresentativeName || "Family representative pending"}
                          </Typography>
                          <Chip size="small" label={plan.planNumber} sx={{ maxWidth: 150, height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: "#F8FAFC" }} />
                          {plan.funeralFileNumber && <Chip size="small" label={plan.funeralFileNumber} sx={{ maxWidth: 185, height: 18, fontSize: 9.5, fontWeight: 850, bgcolor: "#ECFDF5", color: "#047857" }} />}
                        </Stack>
                      </Box>
                    </Stack>
                    <Box textAlign="right" flexShrink={0} sx={{ minWidth: 120 }}>
                      <Typography fontSize={10.5} color={muted} fontWeight={850} sx={{ textTransform: "uppercase", lineHeight: 1 }}>
                        Category
                      </Typography>
                      <Typography fontSize={12.5} color={ink} fontWeight={900} sx={{ mt: 0.35, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {labelize(plan.funeralCategory || "Pending")}
                      </Typography>
                      {plan.bodyLocationName && (
                        <Typography fontSize={11.5} color={muted} fontWeight={750} sx={{ mt: 0.25, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {plan.bodyLocationName}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            {!plans.length && <Box p={3}><Typography color={muted}>No assigned funeral plans found.</Typography></Box>}
          </Stack>
          <StandardPager page={page} total={totalCount} pageSize={pageSize} onPage={(p) => changePage(null as any, p + 1)} />
        </Paper>

        {!selectedPlan ? null : (
          <Box>
            <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: surface, p: 2, mb: 2, boxShadow: softShadow }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box>
                  <Button
                    variant="text"
                    onClick={() => setSelectedPlan(null)}
                    sx={{ color: muted, fontWeight: 850, p: 0, minWidth: 0, mb: 1, textTransform: "none" }}
                  >
                    Back to planning cases
                  </Button>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography color={ink} fontWeight={950} fontSize={24}>{selectedPlan.caseNumber}</Typography>
                    <Chip size="small" label={selectedPlan.planNumber} sx={{ fontWeight: 850, bgcolor: "#F8FAFC" }} />
                    {selectedPlan.funeralFileNumber && <Chip size="small" label={selectedPlan.funeralFileNumber} sx={{ fontWeight: 900, bgcolor: "#ECFDF5", color: "#047857" }} />}
                    <Chip size="small" label={labelize(selectedPlan.status || "")} sx={{ color: statusColor(selectedPlan.status), bgcolor: "#F8FAFC", fontWeight: 900 }} />
                  </Stack>
                  <Stack direction="row" gap={1} mt={1.5} flexWrap="wrap">
                    {[
                      [<PersonOutlineOutlinedIcon fontSize="small" />, selectedPlan.plannerName || "Planner pending"],
                      [<CallOutlinedIcon fontSize="small" />, selectedPlan.familyRepresentativeContact || "No family contact"],
                      [<CalendarMonthOutlinedIcon fontSize="small" />, selectedDate],
                      [<PlaceOutlinedIcon fontSize="small" />, selectedPlan.bodyLocationName || selectedPlan.bodyLocationAddress || "Body location pending"],
                    ].map(([icon, label], index) => (
                      <Chip key={index} icon={icon as React.ReactElement} label={label as string} sx={{ bgcolor: "#F8FAFC", color: muted, fontWeight: 800 }} />
                    ))}
                  </Stack>
                </Box>
                <Stack direction="row" gap={1} flexWrap="wrap">
                </Stack>
              </Box>
            </Paper>

            {isClaimDeclined && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "8px", fontWeight: 850 }}>
                RMA declined the linked claim. Stop all funeral planning activities, commitments and dispatches for this case. This record remains visible for audit only; further actions are blocked.
              </Alert>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: 2, mb: 2 }}>
              <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: surface, p: 2, boxShadow: subtleShadow }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontWeight={950} fontSize={16}>Pre-Funeral Readiness</Typography>
                  <Typography color={orange} fontWeight={950} fontSize={20}>{readiness.percent}%</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={readiness.percent} sx={{ height: 10, borderRadius: 8, bgcolor: "#FFE8D3", "& .MuiLinearProgress-bar": { bgcolor: orange } }} />
                <Typography color={muted} fontSize={13} mt={1}>{readiness.done} of {readiness.total} pre-funeral SOP steps complete. {readiness.blocked} remaining. Post-funeral feedback is tracked separately.</Typography>
                {!!blockers.length && (
                  <Alert severity="warning" sx={{ mt: 1.5, borderRadius: "8px" }}>
                    Blockers: {blockers.map((item: any) => item.title).join(", ")}
                  </Alert>
                )}
              </Paper>

              <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: surface, p: 2, boxShadow: subtleShadow }}>
                <Typography fontWeight={950} fontSize={16} mb={1}>Case Snapshot</Typography>
                {[
                  ["Family Contact", selectedPlan.familyRepresentativeName || "Pending"],
                  ["Category", labelize(selectedPlan.funeralCategory || "Pending")],
                  ["Payment", selectedPlan.paymentConfirmed ? "Confirmed" : "Pending"],
                  ["Docs", `${labelize(selectedPlan.bi1663Status || "not started")} / ${labelize(selectedPlan.deathCertificateStatus || "not started")}`],
                ].map(([label, value]) => (
                  <Box key={label} display="flex" justifyContent="space-between" gap={1} py={0.65} borderBottom={`1px solid ${border}`}>
                    <Typography color={muted} fontSize={12} fontWeight={850}>{label}</Typography>
                    <Typography fontSize={12} fontWeight={900} textAlign="right">{value}</Typography>
                  </Box>
                ))}
              </Paper>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "330px 1fr" }, gap: 2, alignItems: "start" }}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${border}`,
                  borderRadius: "8px",
                  bgcolor: surface,
                  p: 1.5,
                  boxShadow: subtleShadow,
                  position: { xl: "sticky" },
                  top: { xl: 84 },
                  maxHeight: { xl: "calc(100vh - 110px)" },
                  overflow: { xl: "hidden" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography fontWeight={950} fontSize={16} mb={0.5}>Funeral Planning Checklist</Typography>
                <Typography color={muted} fontSize={12} mb={1.5}>Use the SOP steps as a planner checklist. Work can happen in parallel; final submission validates required items.</Typography>
                <Stack
                  gap={1}
                  sx={{
                    overflowY: { xl: "auto" },
                    pr: { xl: 0.5 },
                    pb: { xl: 0.5 },
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 8 },
                    "&::-webkit-scrollbar-track": { bgcolor: "#F8FAFC" },
                  }}
                >
                  {planningSteps.map((item: any) => {
                    const active = selectedWorkstream === item.id;
                    const color = item.done ? green : item.deferred ? muted : item.blocked ? amber : orange;
                    return (
                      <Box
                        key={item.id}
                        onClick={() => {
                          setSelectedWorkstream(item.id);
                        }}
                        sx={{
                          p: 1.25,
                          borderRadius: "8px",
                          border: `1px solid ${active ? color : border}`,
                          bgcolor: active ? "#FFF9F3" : "#fff",
                          cursor: "pointer",
                          opacity: 1,
                          transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
                          "&:hover": { boxShadow: subtleShadow },
                        }}
                      >
                        <Stack direction="row" gap={1} alignItems="flex-start">
                          <Box sx={{ width: 38, height: 38, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: active ? orange : "#F8FAFC", color: active ? "#fff" : color }}>
                            {item.icon}
                          </Box>
                          <Box flex={1}>
                            <Stack direction="row" justifyContent="space-between" gap={1}>
                              <Typography fontWeight={950} fontSize={14}>{item.title}</Typography>
                              <Chip size="small" label={`SOP ${item.sop}`} sx={{ height: 20, fontSize: 10, fontWeight: 850 }} />
                            </Stack>
                            <Typography color={muted} fontSize={12} mt={0.35}>{item.note}</Typography>
                            <Typography color={color} fontSize={12} fontWeight={900} mt={0.6}>
                              {item.done ? "Complete" : item.deferred ? "Available after funeral" : item.blocked ? "Needs attention" : "In progress"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>

              <Box>
                <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: surface, p: 2, mb: 2, boxShadow: subtleShadow }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}>
                    <Box>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Box sx={{ color: orange, display: "flex" }}>{activeWorkstream?.icon}</Box>
                        <Typography fontWeight={950} fontSize={18}>{activeWorkstream?.title}</Typography>
                        <Chip size="small" label={`SOP ${activeWorkstream?.sop}`} sx={{ bgcolor: orangeSoft, color: orange, fontWeight: 900 }} />
                      </Stack>
                      <Typography color={muted} fontSize={13}>{activeWorkstream?.note}</Typography>
                    </Box>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Button variant="outlined" onClick={() => savePlan()} disabled={saving || isClaimDeclined} sx={neutralButtonSx}>Save Draft</Button>
                      {isStep10Like(activeWorkstream?.id || "") && (
                        <Button variant="outlined" onClick={generateHandoverChecklist} disabled={saving || isClaimDeclined} sx={neutralButtonSx}>Generate Handover Checklist</Button>
                      )}
                      <Button variant="contained" onClick={markStepComplete} disabled={saving || isClaimDeclined || Boolean(activeWorkstream?.deferred)} sx={orangeButtonSx}>Mark Step Complete</Button>
                    </Stack>
                  </Stack>
                  <Box sx={{ mb: 1.5, p: 1.5, borderRadius: "8px", bgcolor: "#F8FAFC", border: `1px solid ${border}` }}>
                    <Typography fontSize={12} fontWeight={900} mb={1}>Required for SOP {activeWorkstream?.sop}</Typography>
                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      {(activeWorkstream?.requirements || []).map((requirement: any) => (
                        <Chip key={requirement.label} size="small"
                          label={`${requirement.done ? "✓" : "○"} ${requirement.label}`}
                          sx={{ bgcolor: requirement.done ? "#ECFDF3" : "#FFF7ED", color: requirement.done ? green : amber, fontWeight: 800 }} />
                      ))}
                    </Stack>
                  </Box>
                  {isStep10Like(activeWorkstream?.id || "") && (
                    <Alert severity="info" sx={{ mb: 1.5, borderRadius: "8px" }}>
                      Confirm Funeral is the final pre-funeral action. It starts the linked Warehouse and Funeral Fleet work. Post-funeral feedback is tracked in SOP 11.
                    </Alert>
                  )}
                  {renderWorkstream()}
                </Paper>

                {isStep10Like(activeWorkstream?.id || "") && (
                <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "14px", bgcolor: surface, p: { xs: 1.5, md: 2 }, boxShadow: subtleShadow, textAlign: "left !important" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} gap={1.5} flexWrap="wrap">
                    <Box sx={{ textAlign: "left !important" }}>
                      <Typography fontWeight={950} fontSize={18} textAlign="left">Operational Readiness</Typography>
                      <Typography color={muted} fontSize={13} mt={0.35}>Live department handoffs and progress for this funeral. Operational work continues inside the relevant module.</Typography>
                    </Box>
                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`${(selectedPlan.tasks || []).filter((task: any) => ["REQUESTED", "IN_PROGRESS"].includes(task.taskStatus)).length} active`}
                        sx={{ bgcolor: "#EFF6FF", color: "#1D4ED8", fontWeight: 900 }}
                      />
                      <Chip
                        size="small"
                        label={`${(selectedPlan.tasks || []).filter((task: any) => task.taskStatus === "COMPLETED").length} completed`}
                        sx={{ bgcolor: "#ECFDF3", color: green, fontWeight: 900 }}
                      />
                    </Stack>
                  </Stack>
                  <Box
                    sx={{
                      border: "1px solid #E2E8F0",
                      borderRadius: "12px",
                      overflow: "hidden",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: "none", md: "grid" },
                        gridTemplateColumns: "minmax(230px, 1.25fr) minmax(170px, 0.8fr) minmax(250px, 1.2fr) minmax(175px, 0.65fr)",
                        gap: 1.5,
                        px: 2,
                        py: 1,
                        bgcolor: "#F8FAFC",
                        borderBottom: "1px solid #E2E8F0",
                      }}
                    >
                      {['Department & purpose', 'Responsible team', 'Current state', 'Status & action'].map((heading) => (
                        <Typography key={heading} fontSize={10.5} fontWeight={950} color="#64748B" textTransform="uppercase" letterSpacing={0.45}>
                          {heading}
                        </Typography>
                      ))}
                    </Box>
                    {(selectedPlan.tasks || []).map((task: any) => {
                      const isNotRequired = task.taskStatus === "NOT_REQUIRED";
                      const assignedTeam = task.assignedTo || taskTeamMap[task.taskType];
                      const isRequested = ["REQUESTED", "IN_PROGRESS"].includes(String(task.taskStatus || ""));
                      const isCompleted = task.taskStatus === "COMPLETED";
                      const isSystemManaged = systemManagedTaskTypes.has(task.taskType);
                      const isConfirmed = ["CONFIRMED", "IN_EXECUTION", "FEEDBACK_PENDING", "COMPLETED", "CLOSED"]
                        .includes(String(selectedPlan.status || ""));
                      const statusStyle = isNotRequired
                        ? { background: "#EEF2F7", foreground: "#64748B", accent: "#CBD5E1" }
                        : isCompleted
                          ? { background: "#ECFDF3", foreground: "#15803D", accent: "#22C55E" }
                          : task.delayed
                            ? { background: "#FEF2F2", foreground: "#B91C1C", accent: "#EF4444" }
                            : task.taskStatus === "IN_PROGRESS"
                              ? { background: "#EFF6FF", foreground: "#1D4ED8", accent: "#3B82F6" }
                              : { background: "#FFF7ED", foreground: "#C2410C", accent: orange };
                      const currentState = isNotRequired
                        ? "Not required for this funeral"
                        : isCompleted
                          ? "Department work completed"
                          : task.taskType === "BODY_REMOVAL"
                            ? "Linked to Body Removal; allocation and execution stay in that module"
                            : task.taskType === "WAREHOUSE_PREPARATION" && !isConfirmed
                              ? "Will be created automatically when the funeral is confirmed"
                              : task.taskType === "FLEET" && !isConfirmed
                                ? "Funeral fleet request will be created on confirmation"
                                : task.taskType === "POST_FUNERAL_FEEDBACK"
                                  ? "Scheduled automatically after the funeral"
                                  : isRequested
                                    ? "Request sent; live department status is being tracked"
                                    : isSystemManaged
                                      ? "Managed automatically by the operational module"
                                      : "Planner action is required to request this team";

                      return (
                        <Box
                          key={task.taskType}
                          sx={{
                            position: "relative",
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "minmax(210px, 1.15fr) minmax(145px, 0.72fr) minmax(220px, 1.15fr) minmax(155px, 0.7fr)" },
                            gap: { xs: 1, lg: 1.5 },
                            alignItems: "center",
                            px: { xs: 1.4, md: 2 },
                            py: { xs: 1.4, md: 1.55 },
                            bgcolor: isNotRequired ? "#FAFBFC" : "#FFFFFF",
                            borderBottom: "1px solid #E8EDF3",
                            textAlign: "left",
                            transition: "background-color 160ms ease",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 3,
                              bgcolor: statusStyle.accent,
                            },
                            "&:hover": { bgcolor: isNotRequired ? "#F8FAFC" : "#FCFDFE" },
                            "&:last-of-type": { borderBottom: 0 },
                          }}
                        >
                          <Stack direction="row" alignItems="center" gap={1.1} minWidth={0}>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                flex: "0 0 38px",
                                borderRadius: "10px",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: statusStyle.background,
                                color: statusStyle.foreground,
                              }}
                            >
                              {taskIcons[task.taskType]}
                            </Box>
                            <Box flex={1} minWidth={0}>
                              <Typography fontSize={14.5} fontWeight={950} color={isNotRequired ? "#475569" : ink} lineHeight={1.2}>{labelize(task.taskType)}</Typography>
                              <Typography fontSize={11.5} color="#475569" mt={0.35} lineHeight={1.35}>{task.requestPurpose || "Operational support"}</Typography>
                              {task.referenceNumber && (
                                <Typography fontSize={10.5} color={muted} mt={0.35} noWrap title={task.referenceNumber}>
                                  Ref: {task.referenceNumber}
                                </Typography>
                              )}
                            </Box>
                          </Stack>

                          <Box>
                            <Typography display={{ xs: "block", md: "none" }} fontSize={9.5} fontWeight={950} color={muted} textTransform="uppercase" mb={0.45}>
                              Responsible team
                            </Typography>
                            <Chip
                              size="small"
                              label={isNotRequired ? "No team required" : labelize(assignedTeam || "Unassigned")}
                              sx={{ height: 25, maxWidth: "100%", bgcolor: isNotRequired ? "#EEF2F7" : "#F1F5F9", color: "#334155", fontSize: 10.5, fontWeight: 900 }}
                            />
                          </Box>

                          <Box>
                            <Typography display={{ xs: "block", md: "none" }} fontSize={9.5} fontWeight={950} color={muted} textTransform="uppercase" mb={0.35}>
                              Current state
                            </Typography>
                            <Typography fontSize={11.5} fontWeight={isCompleted ? 900 : 750} color={statusStyle.foreground} lineHeight={1.45}>
                              {currentState}
                            </Typography>
                            {task.lastUpdatedAt && (
                              <Typography fontSize={10} color={muted} mt={0.35}>
                                Updated {dayjs(task.lastUpdatedAt).format("DD MMM, HH:mm")}
                              </Typography>
                            )}
                          </Box>

                          <Stack alignItems={{ xs: "flex-start", md: "flex-end" }} gap={0.75}>
                            <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                              <Chip
                                size="small"
                                label={labelize(task.taskStatus)}
                                sx={{ height: 23, fontSize: 10.5, fontWeight: 900, bgcolor: statusStyle.background, color: statusStyle.foreground }}
                              />
                              {task.delayed && <Chip size="small" label="Follow-up due" sx={{ height: 23, fontSize: 9.5, fontWeight: 900, bgcolor: "#FEE2E2", color: "#B91C1C" }} />}
                            </Stack>

                            {!isNotRequired && !isCompleted && !isSystemManaged && (
                              <Stack direction="row" gap={0.6} flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                              <Button
                                size="small"
                                variant={isRequested ? "outlined" : "contained"}
                                onClick={() => updateTask(task.taskType, "request")}
                                disabled={saving || isRequested || isClaimDeclined}
                                sx={{
                                  ...(isRequested ? neutralButtonSx : orangeButtonSx),
                                  minWidth: 104,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {isRequested ? "Request Sent" : "Request Team"}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                sx={{ ...orangeButtonSx, minWidth: 142, whiteSpace: "nowrap" }}
                                onClick={() => updateTask(task.taskType, "complete")}
                                disabled={saving || !isRequested || isClaimDeclined}
                              >
                                Mark Complete
                              </Button>
                            </Stack>
                            )}
                            {task.followUpAllowed && (
                              <Button
                                size="small"
                                variant={task.delayed ? "contained" : "outlined"}
                                onClick={() => followUpTask(task)}
                                disabled={saving || isClaimDeclined}
                                sx={{
                                  ...(task.delayed ? orangeButtonSx : neutralButtonSx),
                                  minWidth: 104,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Follow Up
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
                )}

                {isStep10Like(activeWorkstream?.id || "") && (
                <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: surface, p: 2, mt: 2, boxShadow: subtleShadow }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                    <Box>
                      <Typography fontWeight={950} fontSize={18}>Confirm Funeral</Typography>
                      <Typography color={muted} fontSize={13}>Validates pre-funeral requirements only, then creates one linked Warehouse Preparation and Funeral Fleet Request. Post-funeral feedback is not required here.</Typography>
                      {!confirmChecklist.canConfirm && (
                        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1}>
                          {confirmChecklist.missing.map((item: string) => (
                            <Chip key={item} size="small" label={item} sx={{ bgcolor: "#FFF7ED", color: amber, fontWeight: 850 }} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {["CONFIRMED", "IN_EXECUTION", "FEEDBACK_PENDING", "COMPLETED", "CLOSED"].includes(String(selectedPlan.status || ""))
                        ? <Chip label="Funeral Confirmed" sx={{ bgcolor: "#ECFDF3", color: green, fontWeight: 900 }} />
                        : <Button variant="contained" onClick={submitPlan} disabled={saving || !confirmChecklist.canConfirm || isClaimDeclined} sx={{ ...orangeButtonSx, px: 2.5 }}>Confirm Funeral</Button>}
                    </Stack>
                  </Stack>
                </Paper>
                )}
                {activeWorkstream?.id === "step11" && ["CONFIRMED", "IN_EXECUTION", "FEEDBACK_PENDING", "COMPLETED"].includes(String(selectedPlan.status || "")) && (
                  <Paper elevation={0} sx={{ border: `1px solid ${border}`, borderRadius: "8px", bgcolor: surface, p: 2, mt: 2, boxShadow: subtleShadow }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                      <Box>
                        <Typography fontWeight={950} fontSize={18}>Complete Case</Typography>
                        <Typography color={muted} fontSize={13}>Capture post-funeral feedback before closing the planning cycle.</Typography>
                      </Box>
                      <Button variant="outlined" onClick={completeCase} disabled={saving || !canCompleteCase} sx={neutralButtonSx}>Complete Case</Button>
                    </Stack>
                    {!canCompleteCase && (
                      <Alert severity="info" sx={{ mt: 1.5, borderRadius: "8px" }}>
                        Feedback notes and the feedback date are required. Backend also checks warehouse return, required catering/tombstone closure and open exceptions.
                      </Alert>
                    )}
                  </Paper>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
